import { useEffect, useRef, useState, Fragment } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Upload, FolderPlus, FileSpreadsheet, FileCode2, ChevronRight, Trash2, Pencil, Sparkles, ListFilter, ChevronDown, Download, Tags, Wand2, ListChecks, Users2, X as XIcon, Copy, Settings, Merge } from "lucide-react";
import { toast } from "sonner";

// Highlight match ricerca (case-insensitive). Ritorna JSX con <mark> sui match.
function highlight(text, q) {
  const s = String(text || "");
  const query = String(q || "").trim();
  if (!query || query.length < 2) return s;
  try {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(${escaped})`, "ig");
    const parts = s.split(re);
    return parts.map((p, i) =>
      re.test(p)
        ? <mark key={i} className="bg-amber-200 dark:bg-amber-500/40 rounded px-0.5">{p}</mark>
        : <span key={i}>{p}</span>
    );
  } catch { return s; }
}
import { useAuth } from "@/context/AuthContext";
import { loadState, saveState } from "@/lib/navState";
import RigaDetail from "@/pages/RigaDetail";

// Stati lavorazione FASE 1 — colori applicati sull'intera riga (inline style per garantire cross-browser)
const STATI_LAV = [
  { key: "non_iniziato",   label: "Non iniziato",   bg: null,        bgDark: null,        dotClass: "bg-slate-300" },
  { key: "in_lavorazione", label: "In lavorazione", bg: "#fef3c7",   bgDark: "#78350f33", dotClass: "bg-amber-500" },
  { key: "da_verificare",  label: "Da verificare",  bg: "#fee2e2",   bgDark: "#7f1d1d33", dotClass: "bg-rose-500" },
  { key: "completato",     label: "Completato",     bg: "#d1fae5",   bgDark: "#064e3b33", dotClass: "bg-emerald-500" },
  { key: "sospeso",        label: "Sospeso",        bg: "#e2e8f0",   bgDark: "#1e293b66", dotClass: "bg-slate-500" },
  { key: "in_valutazione", label: "In valutazione", bg: "#dbeafe",   bgDark: "#1e3a8a33", dotClass: "bg-blue-500" },
  { key: "valutato",       label: "Valutato",       bg: "#ede9fe",   bgDark: "#4c1d9533", dotClass: "bg-violet-500" },
];
const STATO_META = Object.fromEntries(STATI_LAV.map(s => [s.key, s]));

function LoadMoreTrigger({ onIntersect, label, testId }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) onIntersect();
    }, { rootMargin: "300px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [onIntersect]);
  return (
    <div ref={ref} data-testid={testId} className="py-3 text-center text-xs text-slate-500 border-t border-slate-100 dark:border-slate-800">
      <span className="animate-pulse">{label} · scorri per continuare…</span>
    </div>
  );
}


export default function ComputoTab({ cantiere, onComputoChange, onComputiCountChange }) {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canEdit = user && (user.role === "ADMIN" || user.role === "TECNICO");
  const isAdmin = user && user.role === "ADMIN";
  const stateScope = `computo:${cantiere.id}`;
  const restoredRef = useRef(false);

  const [computo, setComputo] = useState(null);
  const [computiList, setComputiList] = useState([]);
  const [selectedComputoId, setSelectedComputoId] = useState(null);
  const [capitoli, setCapitoli] = useState([]);
  const [righePerCap, setRighePerCap] = useState({});
  const [openCap, setOpenCap] = useState([]);
  // Dual view: "xpwe" (albero capitoli originale) | "categorie" (raggruppato per Categoria Operativa).
  // Preferenza persistita in localStorage. NON duplica dati: le righe restano identiche,
  // cambia solo il criterio di raggruppamento e la fetch (per-capitolo vs per-categoria).
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("computo:view_mode") || "xpwe"; } catch { return "xpwe"; }
  });
  const [righePerCat, setRighePerCat] = useState({}); // {[catKey]: {items,total,offset}} — catKey = uuid | "none"
  const [openCat, setOpenCat] = useState([]);
  const [gestCatOpen, setGestCatOpen] = useState(false); // Dialog "Gestisci categorie" (ADMIN)
  const [loading, setLoading] = useState(true);
  const [newComputoOpen, setNewComputoOpen] = useState(false);
  const [newCompForm, setNewCompForm] = useState({ nome: "", descrizione: "" });
  const [newCapOpen, setNewCapOpen] = useState(false);
  const [capForm, setCapForm] = useState({ nome: "", codice: "" });
  const [newRigaOpen, setNewRigaOpen] = useState(false);
  const [activeCapId, setActiveCapId] = useState(null);
  const [rigaForm, setRigaForm] = useState({ num_ord: "", tariffa: "", designazione: "", unita_misura: "", quantita: "", prezzo_unitario: "" });
  const [exportingSezione, setExportingSezione] = useState(null);
  // Modal di scelta tipo_costo per l'export "Preventivo + descrizione + costi INVILUPPO"
  const [invCostoModalOpen, setInvCostoModalOpen] = useState(false);
  const [invCostoTipo, setInvCostoTipo] = useState("totale");

  const downloadExport = async (sezione, label, categoria_operativa_id = null, extraParams = null) => {
    const stateKey = categoria_operativa_id ? `cat-${categoria_operativa_id}` : sezione;
    setExportingSezione(stateKey);
    try {
      const params = { sezione };
      // FIX MULTI-COMPUTO: passa SEMPRE il computo attualmente selezionato in UI.
      // Senza questo, il backend cadeva in `_computo_active_for_cantiere` che
      // sceglie l'ultimo attivo per versione DESC → esportava sempre lo stesso
      // computo (es. Superbonus) ignorando la selezione dell'utente (Sisma).
      if (computo?.id) params.computo_id = computo.id;
      if (categoria_operativa_id) params.categoria_operativa_id = categoria_operativa_id;
      if (extraParams && typeof extraParams === "object") Object.assign(params, extraParams);
      const res = await api.get(`/cantieri/${cantiere.id}/export-computo.xlsx`, {
        params,
        responseType: "blob",
      });
      // Estrai filename dall'header (fallback a nome sensato)
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
      const fname = match ? match[1] : `export_${sezione}_${cantiere.id}.xlsx`;
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Esportato: ${label}`);
    } catch (e) {
      // Il body è un blob → prova a leggerlo come testo per estrarre detail JSON
      let detail = null;
      try {
        if (e.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { detail = JSON.parse(txt)?.detail; } catch { detail = txt; }
        } else detail = e.response?.data?.detail;
      } catch {/* noop */}
      const status = e.response?.status;
      if (detail) toast.error(String(detail));
      else if (status) toast.error(`Errore HTTP ${status} durante export${e.message ? " · " + e.message : ""}`);
      else toast.error(`Errore di rete durante export${e.message ? ": " + e.message : ""}`);
    } finally {
      setExportingSezione(null);
    }
  };
  const [statoFilter, setStatoFilter] = useState("all");
  const [categoriaFilter, setCategoriaFilter] = useState("all"); // "all" | "none" | <cat_uuid>
  const [categorie, setCategorie] = useState([]);
  const [categorieById, setCategorieById] = useState({});
  const [categorieBreakdown, setCategorieBreakdown] = useState(null); // {per_categoria:[{categoria_id,codice,nome,righe_considerate}], totale:{...}} — usato dal popover MDO
  const [categorieRiepilogo, setCategorieRiepilogo] = useState(null); // {totale_righe, classificate, da_classificare, categorie:[]} — usato dalla CategorieBar
  const [classifyRunning, setClassifyRunning] = useState(false);
  const [revisioneOpen, setRevisioneOpen] = useState(false);
  const [subsAssegnaOpen, setSubsAssegnaOpen] = useState(false);
  const [catSubsSummary, setCatSubsSummary] = useState(null); // {assegnate, totali}
  const [summary, setSummary] = useState(null);
  const [expandedRiga, setExpandedRiga] = useState(null); // id of expanded riga (one at a time)
  // Ricerca nel computo aperto (solo)
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null=off, {items,total,byCap}
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1); // -1 = nessun risultato selezionato

  const loadSummary = async () => {
    try { const { data } = await api.get(`/cantieri/${cantiere.id}/stato-summary`); setSummary(data); }
    catch (e) { console.warn("stato-summary fetch failed:", e?.message || e); }
  };

  // Carica categorie operative attive per select/filtro/badge.
  const loadCategorie = async () => {
    try {
      const { data } = await api.get(`/categorie-operative`);
      setCategorie(data || []);
      setCategorieById(Object.fromEntries((data || []).map(c => [c.id, c])));
    } catch (e) { console.warn("categorie-operative fetch failed:", e?.message); }
  };

  // Riepilogo classificazione (nuovo endpoint dedicato): conta TUTTE le righe
  // lavorazione, indipendentemente da IncMDO/prezzo/quantità XPWE. Usato solo
  // dalla `CategorieBar`. Il popover MDO continua ad usare il breakdown MDO.
  const loadCategorieRiepilogo = async (computoId) => {
    if (!computoId) return;
    try {
      const { data } = await api.get(`/computi/${computoId}/categorie-riepilogo`);
      setCategorieRiepilogo(data || null);
    } catch (e) { console.warn("categorie riepilogo fetch failed:", e?.message); }
  };

  // Riepilogo assegnazione Categorie ↔ Subappaltatori (Task 4A). Usato dalla
  // CategorieBar per mostrare "X/Y categorie assegnate".
  const loadCatSubsSummary = async (computoId) => {
    if (!computoId) return;
    try {
      const { data } = await api.get(`/computi/${computoId}/categorie-subappaltatori`);
      const cats = (data?.categorie || []).filter(c => c.assegnabile);
      const assegnate = cats.filter(c => (c.subappaltatori || []).length > 0).length;
      setCatSubsSummary({ assegnate, totali: cats.length });
    } catch (e) { console.warn("cat-subs summary fetch failed:", e?.message); }
  };

  // Riepilogo classificazione: usa GET /mdo-rules-categorie che già restituisce
  // per_categoria con righe_considerate + nome/codice. Non creiamo endpoint nuovi.
  const loadCategorieBreakdown = async (computoId) => {
    if (!computoId) return;
    try {
      const { data } = await api.get(`/computi/${computoId}/mdo-rules-categorie`);
      setCategorieBreakdown(data || null);
    } catch (e) { console.warn("categorie breakdown fetch failed:", e?.message); }
  };

  const loadComputo = async () => {
    setLoading(true);
    try {
      // MULTI-COMPUTO: prendi elenco. Se >1, permetti selezione; sennò
      // comportamento identico al pre-esistente (apre l'attivo più recente).
      const { data: list } = await api.get(`/cantieri/${cantiere.id}/computi`);
      setComputiList(Array.isArray(list) ? list : []);
      let selected = null;
      if (Array.isArray(list) && list.length > 0) {
        // FASE C.2.1 — Hook post-import XPWE: se presente ?open_inviluppo_auto=<id>
        // pre-seleziona quel computo (il badge si occupa poi di aprire la Preview).
        const invAutoId = searchParams.get("open_inviluppo_auto");
        if (invAutoId) {
          selected = list.find(c => c.id === invAutoId) || null;
        }
        // preferisci quello ancora attivo con versione più alta o quello già selezionato
        if (!selected && selectedComputoId) {
          selected = list.find(c => c.id === selectedComputoId) || null;
        }
        // E6.1 — computo scelto dall'utente conservato nell'URL (?computo=) dopo il ricaricamento
        if (!selected && searchParams.get("computo")) {
          selected = list.find(c => c.id === searchParams.get("computo")) || null;
        }
        if (!selected) {
          const active = list.filter(c => c.is_active);
          // il predefinito resta il computo storico: i computi Excel si aprono solo se scelti
          selected = (active.find(c => c.source_type !== "xlsx_analisi") || active[0] || list[0]);
        }
      }
      if (selected) {
        // hydrate the current 'computo' shape leggendo il dettaglio via endpoint compat
        const { data } = await api.get(`/cantieri/${cantiere.id}/computo`).catch(() => ({ data: selected }));
        // Se il get_active_computo torna un computo diverso da quello selezionato,
        // preferisci comunque quello selezionato dall'utente (multi-computo).
        setComputo(data && data.id === selected.id ? data : selected);
        setSelectedComputoId(selected.id);
        await loadCapitoli(selected.id);
      } else {
        setComputo(null);
        setCapitoli([]);
      }
    } catch (e) {
      setComputo(null);
    } finally { setLoading(false); }
  };

  const switchComputo = async (computoId) => {
    setSelectedComputoId(computoId);
    const sp = new URLSearchParams(window.location.search);
    sp.set("computo", computoId);
    setSearchParams(sp, { replace: true });
    setLoading(true);
    try {
      const c = computiList.find(x => x.id === computoId) || null;
      setComputo(c);
      setRighePerCap({});
      setRighePerCat({});
      setOpenCap([]);
      setOpenCat([]);
      if (c) await loadCapitoli(c.id);
    } finally { setLoading(false); }
  };

  const renameComputo = async (computoId, oldName) => {
    const n = window.prompt("Nuovo nome computo:", oldName || "");
    if (n === null) return;
    const nome = n.trim();
    if (!nome) { toast.error("Il nome non può essere vuoto"); return; }
    try {
      await api.patch(`/computi/${computoId}`, { nome });
      toast.success("Computo rinominato");
      await loadComputo();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore rinomina"); }
  };

  const deleteComputo = async (computoId, name) => {
    const conferma = window.confirm(`Eliminare il computo "${name}"? L'azione è reversibile (soft delete: il computo viene disattivato ma nulla viene rimosso fisicamente).`);
    if (!conferma) return;
    try {
      await api.delete(`/computi/${computoId}`);
      toast.success("Computo eliminato");
      // resetta selezione e ricarica
      setSelectedComputoId(null);
      await loadComputo();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };
  // Notifica il parent (CantiereDetail) quando cambia il computo selezionato o
  // il conteggio complessivo. Le sezioni collegate (Ordini, MdO, Costi, Foto,
  // Note, Ticket) usano questi valori per filtrare le proprie GET su computo_id.
  useEffect(() => {
    if (typeof onComputoChange === "function") {
      onComputoChange(computo ? { id: computo.id, nome: computo.nome, versione: computo.versione, source_type: computo.source_type } : null);
    }
  }, [computo?.id]);
  useEffect(() => {
    if (typeof onComputiCountChange === "function") {
      onComputiCountChange(computiList.length);
    }
  }, [computiList.length]);
  const loadCapitoli = async (computoId) => {
    const { data } = await api.get(`/computi/${computoId}/capitoli`);
    setCapitoli(data);
  };
  const loadRighe = async (capId, opts = {}) => {
    const { offset = 0, q = "", append = false, stato = null, categoria = null } = opts;
    let effStato;
    if (stato !== null) effStato = stato;
    else if (statoFilter !== "all") effStato = statoFilter;
    else effStato = undefined;
    let effCat;
    if (categoria !== null) effCat = categoria;
    else if (categoriaFilter !== "all") effCat = categoriaFilter;
    else effCat = undefined;
    const res = await api.get(`/capitoli/${capId}/righe`, {
      params: { offset, limit: 50, q: q || undefined, stato: effStato, categoria_operativa_id: effCat },
    });
    const data = res.data;
    const items = Array.isArray(data) ? data : (data.items || []);
    const total = Array.isArray(data) ? items.length : (data.total || items.length);
    setRighePerCap(prev => {
      const prevRows = append ? (prev[capId]?.items || []) : [];
      return { ...prev, [capId]: { items: [...prevRows, ...items], total, offset: offset + items.length, q, stato: effStato || null, categoria: effCat || null } };
    });
  };
  const changeRigaStato = async (rigaId, capId, nuovoStato) => {
    // salva locale immediato per no-reload
    setRighePerCap(prev => {
      const cur = prev[capId]; if (!cur) return prev;
      return { ...prev, [capId]: { ...cur, items: cur.items.map(r => r.id === rigaId ? { ...r, stato_lavorazione: nuovoStato } : r) } };
    });
    try {
      await api.patch(`/righe/${rigaId}/stato`, { stato_lavorazione: nuovoStato });
      loadSummary();
    } catch (e) { toast.error("Errore salvataggio stato"); loadRighe(capId, { offset: 0, append: false }); }
  };
  const loadMoreRighe = async (capId) => {
    const st = righePerCap[capId];
    if (!st || st.items.length >= st.total) return;
    await loadRighe(capId, { offset: st.offset, q: st.q, append: true });
  };

  // === Vista per Categoria Operativa (dual view) ===
  // Riusa endpoint esistente /computi/{id}/righe/search: passa `categoria_operativa_id`,
  // paginazione via offset/limit. Non introduce nuove tabelle o duplicazione dati.
  const loadRigheCat = async (catKey, opts = {}) => {
    if (!computo?.id) return;
    const { offset = 0, append = false } = opts;
    const params = { limit: 50, offset };
    if (catKey === "none") params.categoria_operativa_id = "none";
    else params.categoria_operativa_id = catKey;
    try {
      const { data } = await api.get(`/computi/${computo.id}/righe/search`, { params });
      let items = data.items || [];
      // Filtro stato lato client (endpoint search non lo supporta): preserva coerenza con toolbar.
      if (statoFilter !== "all") items = items.filter(r => (r.stato_lavorazione || "non_iniziato") === statoFilter);
      const total = data.total ?? items.length;
      setRighePerCat(prev => {
        const prevRows = append ? (prev[catKey]?.items || []) : [];
        return { ...prev, [catKey]: { items: [...prevRows, ...items], total, offset: offset + items.length } };
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento righe categoria"); }
  };
  const loadMoreRigheCat = async (catKey) => {
    const st = righePerCat[catKey];
    if (!st || st.items.length >= st.total) return;
    await loadRigheCat(catKey, { offset: st.offset, append: true });
  };
  const changeRigaStatoCat = async (rigaId, catKey, nuovoStato) => {
    setRighePerCat(prev => {
      const cur = prev[catKey]; if (!cur) return prev;
      return { ...prev, [catKey]: { ...cur, items: cur.items.map(r => r.id === rigaId ? { ...r, stato_lavorazione: nuovoStato } : r) } };
    });
    try {
      await api.patch(`/righe/${rigaId}/stato`, { stato_lavorazione: nuovoStato });
      loadSummary();
    } catch (e) { toast.error("Errore salvataggio stato"); loadRigheCat(catKey, { offset: 0, append: false }); }
  };

  // Persistenza preferenza vista
  useEffect(() => {
    try { localStorage.setItem("computo:view_mode", viewMode); } catch { /* noop */ }
  }, [viewMode]);

  // ============================================================
  // Spostamento LIVE riga fra categorie nella vista Categorie.
  // Chiamato da RigaDetail dopo PATCH categoria riuscito.
  // - Rimuove la riga da righePerCat[old] e la inserisce in righePerCat[new]
  // - Aggiorna contatori in categorieRiepilogo
  // La riga esiste in una SOLA categoria alla volta.
  // ============================================================
  const handleRigaCategoriaChanged = ({ rigaId, oldCatId, newCatId, manuale }) => {
    const oldKey = oldCatId || "none";
    const newKey = newCatId || "none";
    if (oldKey === newKey) return;

    setRighePerCat(prev => {
      const next = { ...prev };
      // rimuovi da old
      const oldCat = prev[oldKey];
      let moved = null;
      if (oldCat) {
        const items = oldCat.items.filter(r => {
          if (r.id === rigaId) { moved = r; return false; }
          return true;
        });
        next[oldKey] = { ...oldCat, items, total: Math.max(0, (oldCat.total || items.length) - (moved ? 1 : 0)) };
      }
      // inserisci in new (se già caricata)
      const newCat = prev[newKey];
      if (newCat && moved) {
        const updated = { ...moved, categoria_operativa_id: newCatId, categoria_operativa_manuale: !!manuale };
        // evita duplicati
        if (!newCat.items.some(r => r.id === rigaId)) {
          next[newKey] = { ...newCat, items: [updated, ...newCat.items], total: (newCat.total || newCat.items.length) + 1 };
        }
      }
      return next;
    });

    // aggiorna contatori nel riepilogo
    setCategorieRiepilogo(prev => {
      if (!prev) return prev;
      const cats = (prev.categorie || []).map(c => {
        if (String(c.categoria_id) === String(oldKey)) return { ...c, numero_righe: Math.max(0, c.numero_righe - 1) };
        if (String(c.categoria_id) === String(newKey)) return { ...c, numero_righe: c.numero_righe + 1 };
        return c;
      });
      let senza = prev.senza_categoria || 0;
      if (oldKey === "none") senza = Math.max(0, senza - 1);
      if (newKey === "none") senza += 1;
      const cls = (prev.classificate || 0) + ((oldKey === "none" && newKey !== "none") ? 1 : (oldKey !== "none" && newKey === "none") ? -1 : 0);
      return { ...prev, categorie: cats, senza_categoria: senza, classificate: cls };
    });
  };

  useEffect(() => { loadComputo(); loadSummary(); loadCategorie(); }, [cantiere.id]);
  useEffect(() => { if (computo?.id) { loadCategorieBreakdown(computo.id); loadCategorieRiepilogo(computo.id); loadCatSubsSummary(computo.id); } }, [computo?.id]);
  useEffect(() => {
    // ricarica capitoli aperti quando cambia filtro stato o categoria
    if (!openCap.length) return;
    openCap.forEach(cid => loadRighe(cid, { offset: 0, append: false }));
    // eslint-disable-next-line
  }, [statoFilter, categoriaFilter]);
  useEffect(() => {
    // ricarica categorie aperte quando cambia filtro stato
    if (viewMode !== "categorie" || !openCat.length) return;
    openCat.forEach(ck => loadRigheCat(ck, { offset: 0, append: false }));
    // eslint-disable-next-line
  }, [statoFilter, viewMode]);

  // Ricerca nel computo aperto (debounced 300ms). Riusa endpoint /computi/{id}/righe/search.
  useEffect(() => {
    if (!computo?.id) return;
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults(null); setCurrentSearchIndex(-1); return; }
    const h = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const catParam = categoriaFilter !== "all" ? categoriaFilter : undefined;
        const { data } = await api.get(`/computi/${computo.id}/righe/search`, { params: { q, limit: 200, categoria_operativa_id: catParam } });
        let items = data.items || [];
        if (statoFilter !== "all") items = items.filter(r => (r.stato_lavorazione || "non_iniziato") === statoFilter);
        const capById = Object.fromEntries((capitoli || []).map(c => [c.id, c]));
        const capsToOpen = new Set();
        const byCap = {};
        const catsToOpen = new Set(); // categorie da aprire (vista Categorie)
        const byCat = {};
        for (const r of items) {
          (byCap[r.capitolo_id] ||= []).push(r);
          let cur = capById[r.capitolo_id];
          while (cur) { capsToOpen.add(cur.id); cur = cur.parent_id ? capById[cur.parent_id] : null; }
          const catKey = r.categoria_operativa_id || "none";
          (byCat[catKey] ||= []).push(r);
          catsToOpen.add(catKey);
        }
        setSearchResults({ items, total: items.length, byCap, byCat });
        setCurrentSearchIndex(-1);
        // Apri sia i capitoli XPWE che le categorie operative coinvolte
        setOpenCap(prev => Array.from(new Set([...(prev || []), ...capsToOpen])));
        setOpenCat(prev => Array.from(new Set([...(prev || []), ...catsToOpen])));
        // Carica righe per la vista XPWE (endpoint accetta q)
        for (const capId of Object.keys(byCap)) {
          loadRighe(capId, { offset: 0, append: false, q });
        }
        // Vista Categorie: loadRigheCat NON accetta q. Popolo direttamente
        // righePerCat con i risultati di ricerca già raggruppati per categoria.
        // Così il DOM di ogni AccordionContent contiene le righe cercate.
        setRighePerCat(prev => {
          const next = { ...prev };
          for (const catKey of catsToOpen) {
            const its = byCat[catKey] || [];
            next[catKey] = { items: its, total: its.length, offset: its.length };
          }
          return next;
        });
      } catch (e) {
        setSearchResults({ items: [], total: 0, byCap: {}, byCat: {} });
        console.warn("search fail:", e?.message);
      } finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line
  }, [searchQ, statoFilter, categoriaFilter, capitoli.length, computo?.id]);

  const clearSearch = () => {
    setSearchQ("");
    setSearchResults(null);
    setCurrentSearchIndex(-1);
    Object.keys(righePerCap).forEach(cid => loadRighe(cid, { offset: 0, append: false, q: "" }));
    // Ricarica anche le categorie aperte (ricerca non filtra righePerCat, ma
    // le liste in memoria possono contenere solo il subset della ricerca — reload pulito)
    Object.keys(righePerCat).forEach(ck => loadRigheCat(ck, { offset: 0, append: false }));
  };

  // Naviga al risultato di indice `idx`: assicura che gli antenati siano aperti,
  // poi scroll+highlight. Retry breve con rAF se il DOM non è ancora pronto.
  const gotoResult = (idx) => {
    const items = searchResults?.items || [];
    if (!items.length) return;
    const normalizedIdx = ((idx % items.length) + items.length) % items.length;
    const target = items[normalizedIdx];
    setCurrentSearchIndex(normalizedIdx);
    // Assicura antenati capitolo XPWE aperti
    const capById = Object.fromEntries((capitoli || []).map(c => [c.id, c]));
    const toOpen = new Set();
    let cur = capById[target.capitolo_id];
    while (cur) { toOpen.add(cur.id); cur = cur.parent_id ? capById[cur.parent_id] : null; }
    setOpenCap(prev => Array.from(new Set([...(prev || []), ...toOpen])));
    // Assicura categoria aperta (vista Categorie)
    const targetCatKey = target.categoria_operativa_id || "none";
    setOpenCat(prev => prev.includes(targetCatKey) ? prev : [...prev, targetCatKey]);
    // Se la categoria non ha ancora le righe della ricerca in memoria, popola da byCat
    if (searchResults?.byCat?.[targetCatKey] && !righePerCat[targetCatKey]?.items?.some(r => r.id === target.id)) {
      const its = searchResults.byCat[targetCatKey];
      setRighePerCat(prev => ({ ...prev, [targetCatKey]: { items: its, total: its.length, offset: its.length } }));
    }

    // Scroll dopo che il DOM è pronto (retry esteso a 60 tentativi ≈ 1s
    // perché la vista Categorie deve prima ricevere gli items da API)
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(`riga-computo-${target.id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-amber-500", "bg-amber-50", "dark:bg-amber-500/10");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-amber-500", "bg-amber-50", "dark:bg-amber-500/10");
        }, 1500);
        return;
      }
      if (attempts++ < 60) requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
  };

  const onSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const items = searchResults?.items || [];
    if (!items.length) return;
    gotoResult(currentSearchIndex < 0 ? 0 : currentSearchIndex + 1);
  };

  // Restore stato salvato quando torno da RigaDetail
  useEffect(() => {
    if (restoredRef.current || !capitoli.length) return;
    const s = loadState(stateScope);
    if (!s) return;
    restoredRef.current = true;
    if (s.openCap?.length) {
      setOpenCap(s.openCap);
      // carica righe per ogni capitolo aperto
      s.openCap.forEach(cid => { if (!righePerCap[cid]?.items?.length) loadRighe(cid); });
    }
    // scroll deferred (lascia render tabella righe)
    setTimeout(() => {
      if (s.rigaId) {
        const el = document.querySelector(`[data-testid="riga-row-${s.rigaId}"]`);
        if (el) el.scrollIntoView({ block: "center", behavior: "instant" });
        else if (s.scrollY != null) window.scrollTo({ top: s.scrollY, behavior: "instant" });
      } else if (s.scrollY != null) window.scrollTo({ top: s.scrollY, behavior: "instant" });
    }, 400);
  }, [capitoli.length]);

  const goToRiga = (rigaId, capId) => {
    saveState(stateScope, { openCap: openCap.length ? openCap : [capId], rigaId, scrollY: window.scrollY });
    navigate(`/g/${groupId}/cantieri/${cantiere.id}/righe/${rigaId}`);
  };

  const createComputo = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/cantieri/${cantiere.id}/computo`, newCompForm);
      toast.success("Computo creato");
      setNewComputoOpen(false); setNewCompForm({ nome: "", descrizione: "" });
      loadComputo();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const createCap = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/computi/${computo.id}/capitoli`, capForm);
      toast.success("Capitolo creato");
      setNewCapOpen(false); setCapForm({ nome: "", codice: "" });
      loadCapitoli(computo.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const deleteCap = async (id) => {
    if (!confirm("Eliminare il capitolo e tutte le sue righe?")) return;
    try { await api.delete(`/capitoli/${id}`); toast.success("Capitolo eliminato"); loadCapitoli(computo.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const openNewRiga = (capId) => {
    setActiveCapId(capId);
    setRigaForm({ num_ord: "", tariffa: "", designazione: "", unita_misura: "", quantita: "", prezzo_unitario: "" });
    setNewRigaOpen(true);
  };
  const createRiga = async (e) => {
    e.preventDefault();
    const payload = { capitolo_id: activeCapId, ...rigaForm };
    Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
    payload.designazione = rigaForm.designazione;
    try {
      await api.post(`/capitoli/${activeCapId}/righe`, payload);
      toast.success("Riga creata");
      setNewRigaOpen(false);
      loadRighe(activeCapId, { offset: 0, append: false });
      loadCapitoli(computo.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const deleteRiga = async (id, capId) => {
    if (!confirm("Eliminare la riga di computo?")) return;
    try { await api.delete(`/righe/${id}`); toast.success("Riga eliminata"); loadRighe(capId, { offset: 0, append: false }); loadCapitoli(computo.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Rendering righe (riusato da vista XPWE e vista Categorie).
  // Ritorna array di elementi <Fragment> con tr + eventuale tr di dettaglio inline.
  // NON duplica logica: le stesse azioni (stato, delete, expand) rimangono.
  const renderRigheRows = (items, { onStatoChange, onDelete }) => {
    return items.map(r => {
      const meta = STATO_META[r.stato_lavorazione || "non_iniziato"];
      const rowBg = !r.is_header && meta?.bg ? meta.bg : undefined;
      const isExpanded = expandedRiga === r.id;
      return (
        <Fragment key={r.id}>
          <tr id={`riga-computo-${r.id}`} data-testid={`riga-row-${r.id}`}
              data-stato={r.is_header ? "" : (r.stato_lavorazione || "non_iniziato")}
              style={rowBg ? { backgroundColor: rowBg } : undefined}
              className={`border-b border-slate-100 dark:border-slate-800 transition-colors ${r.is_header ? "font-bold bg-slate-50 dark:bg-slate-900" : ""}`}>
            <td className="py-2 px-2 font-mono text-xs">{r.num_ord || "—"}</td>
            <td className="py-2 px-2 font-mono text-xs">{highlight(r.tariffa || "—", searchQ)}</td>
            <td className="py-2 px-2 max-w-lg">
              <div>{highlight(r.designazione || "", searchQ)}</div>
              {!r.is_header && r.categoria_operativa_id && categorieById[r.categoria_operativa_id] && (
                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                  <span
                    className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${categorieById[r.categoria_operativa_id].codice === "DA_CLASSIFICARE"
                      ? "bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/40"
                      : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}
                    data-testid={`riga-categoria-${r.id}`}
                    title={categorieById[r.categoria_operativa_id].nome}
                  >
                    <Tags className="h-2.5 w-2.5" />
                    {categorieById[r.categoria_operativa_id].nome}
                  </span>
                  {r.categoria_operativa_manuale && (
                    <span className="text-[9px] uppercase tracking-widest text-slate-500 border border-slate-300 dark:border-slate-700 rounded px-1 py-[1px]" title="Assegnata manualmente" data-testid={`riga-categoria-manuale-${r.id}`}>Manuale</span>
                  )}
                </div>
              )}
              {!r.is_header && !r.categoria_operativa_id && (
                <div className="mt-0.5">
                  <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-slate-400 border border-dashed border-slate-300 dark:border-slate-700" data-testid={`riga-categoria-vuota-${r.id}`}>
                    <Tags className="h-2.5 w-2.5" /> senza categoria
                  </span>
                </div>
              )}
            </td>
            <td className="py-2 px-2 text-xs">{r.unita_misura || "—"}</td>
            <td className="py-2 px-2 text-right font-mono">{fmt(r.quantita)}</td>
            <td className="py-2 px-2 text-right font-mono">{fmt(r.prezzo_unitario)}</td>
            <td className="py-2 px-2 text-right font-mono font-semibold">{fmt(r.importo_totale)}</td>
            <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
              {r.is_header ? (
                <span className="text-xs text-slate-400">—</span>
              ) : canEdit ? (
                <select value={r.stato_lavorazione || "non_iniziato"} onChange={(e) => onStatoChange(r.id, e.target.value)}
                  data-testid={`stato-select-${r.id}`}
                  className="w-full min-w-[9rem] text-sm bg-white dark:bg-slate-900 border border-slate-400 dark:border-slate-600 rounded-md px-2 py-1 shadow-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500">
                  {STATI_LAV.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              ) : (
                <span className="flex items-center gap-1 text-xs"><span className={`w-2 h-2 rounded-full ${meta?.dotClass}`} />{meta?.label}</span>
              )}
            </td>
            <td className="py-2 px-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
              {!r.is_header && (
                <Button size="icon" variant="ghost" onClick={() => setExpandedRiga(isExpanded ? null : r.id)} title={isExpanded ? "Chiudi" : "Espandi dettaglio"} data-testid={`expand-riga-${r.id}`} className={isExpanded ? "text-amber-600" : ""}>
                  {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </Button>
              )}
              {isAdmin && !r.is_header && onDelete && <Button size="icon" variant="ghost" onClick={() => onDelete(r.id)} data-testid={`del-riga-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}
            </td>
          </tr>
          {isExpanded && (
            <tr className="bg-slate-50/60 dark:bg-slate-900/40" data-testid={`riga-inline-detail-${r.id}`}>
              <td colSpan={9} className="p-4 border-l-4 border-amber-500">
                <RigaDetail rigaIdProp={r.id} cantiereIdProp={cantiere.id} groupIdProp={groupId} inline
                  onCategoriaChanged={handleRigaCategoriaChanged} />
              </td>
            </tr>
          )}
        </Fragment>
      );
    });
  };

  if (loading) return <div className="text-slate-500 p-4">Caricamento computo…</div>;

  if (!computo) {
    return (
      <Card>
        <CardContent className="p-10 text-center">
          <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-400 mb-3" />
          <h3 className="text-lg font-semibold">Nessun computo</h3>
                <p className="text-slate-500 mt-1 max-w-md mx-auto">Crea un computo vuoto per aggiungere manualmente capitoli e righe, oppure importa un file XLSX.</p>
          {canEdit && (
            <div className="mt-6 flex justify-center gap-3">
              <Button onClick={() => setNewComputoOpen(true)} data-testid="new-computo-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Nuovo computo
              </Button>
              {isAdmin && (<>
              <Button variant="outline" onClick={() => navigate(`/g/${groupId}/cantieri/${cantiere.id}/import`)} data-testid="import-xlsx-btn">
                <Upload className="h-4 w-4 mr-2" /> Importa XLSX
              </Button>
              <Button variant="outline" onClick={() => navigate(`/g/${groupId}/cantieri/${cantiere.id}/import-xpwe`)} data-testid="import-xpwe-btn">
                <FileCode2 className="h-4 w-4 mr-2" /> Importa XPWE
              </Button>
              <Button variant="outline" onClick={() => navigate(`/g/${groupId}/cantieri/${cantiere.id}/import-excel-analisi`)} data-testid="import-excel-analisi-btn">
                <Upload className="h-4 w-4 mr-2" /> Excel con analisi
              </Button>
              </>)}
            </div>
          )}
        </CardContent>
        <Dialog open={newComputoOpen} onOpenChange={setNewComputoOpen}>
          <DialogContent><DialogHeader><DialogTitle>Nuovo computo</DialogTitle><DialogDescription>Crea un computo vuoto associato al cantiere.</DialogDescription></DialogHeader>
            <form onSubmit={createComputo} className="space-y-4">
              <div><Label>Nome</Label><Input required value={newCompForm.nome} onChange={(e) => setNewCompForm({ ...newCompForm, nome: e.target.value })} data-testid="computo-nome-input" /></div>
              <div><Label>Descrizione</Label><Textarea value={newCompForm.descrizione} onChange={(e) => setNewCompForm({ ...newCompForm, descrizione: e.target.value })} data-testid="computo-desc-input" /></div>
              <DialogFooter><Button type="submit" data-testid="computo-create-submit" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">Crea</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </Card>
    );
  }

  // Totale computo: sommo SOLO i capitoli radice (parent_id == null).
  const totaleComputo = capitoli
    .filter(c => !c.parent_id)
    .reduce((s, c) => s + Number(c.importo_totale_capitolo || 0), 0);

  // Costruzione tree ricorsiva by parent_id (indipendente dall'ordine backend).
  // Ordina i figli per `ordine` a ciascun livello. DFS: padre → figli.
  const capitoliDFS = (() => {
    const byParent = new Map();
    capitoli.forEach(c => {
      const p = c.parent_id || null;
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(c);
    });
    for (const arr of byParent.values()) arr.sort((a, b) => (a.ordine || 0) - (b.ordine || 0) || String(a.nome || "").localeCompare(String(b.nome || "")));
    const out = [];
    const walk = (pid, depth) => {
      const kids = byParent.get(pid) || [];
      for (const k of kids) { out.push({ ...k, _depth: depth }); walk(k.id, depth + 1); }
    };
    walk(null, 0);
    if (out.length !== capitoli.length) {
      const seen = new Set(out.map(x => x.id));
      capitoli.filter(c => !seen.has(c.id)).forEach(c => out.push({ ...c, _depth: c.livello || 0 }));
    }
    return out;
  })();

  return (
    <div className="space-y-6">
      {/* Selettore multi-computo — visibile quando il cantiere ha ≥1 computo */}
      {computiList.length > 0 && (
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
            <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">
              Computi del cantiere ({computiList.length})
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/g/${groupId}/cantieri/${cantiere.id}/import`)} data-testid="add-computo-xlsx">
                  <Upload className="h-3.5 w-3.5 mr-1" /> + XLSX
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/g/${groupId}/cantieri/${cantiere.id}/import-xpwe`)} data-testid="add-computo-xpwe">
                  <FileCode2 className="h-3.5 w-3.5 mr-1" /> + XPWE
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/g/${groupId}/cantieri/${cantiere.id}/import-excel-analisi`)} data-testid="add-computo-excel-analisi">
                  <Upload className="h-3.5 w-3.5 mr-1" /> + Excel analisi
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {computiList.map(c => {
              const selected = c.id === (computo?.id || selectedComputoId);
              return (
                <div key={c.id}
                     className={`group flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${selected ? "border-amber-500 bg-amber-500/10" : "border-slate-200 dark:border-slate-800 hover:border-amber-400"}`}
                     onClick={() => !selected && switchComputo(c.id)}
                     data-testid={`computo-card-${c.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate max-w-[280px]" title={c.nome}>{c.nome}</div>
                    <div className="text-[11px] text-slate-500 font-mono">
                      € {Number(c.totale_computo || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {c.num_righe} righe · v.{c.versione}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); renameComputo(c.id, c.nome); }}
                        className="text-xs text-slate-500 hover:text-amber-600 px-1"
                        title="Rinomina"
                        data-testid={`rename-computo-${c.id}`}
                      >✎</button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteComputo(c.id, c.nome); }}
                        className="text-xs text-slate-500 hover:text-rose-600 px-1"
                        title="Elimina computo"
                        data-testid={`delete-computo-${c.id}`}
                      >✕</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {computiList.length > 1 && (
            <div className="mt-2 text-[11px] text-slate-500 italic">
              Multi-computo: ogni computo è indipendente. Materiali, ordini, MdO, ticket restano collegati alla riga originaria.
            </div>
          )}
        </CardContent></Card>
      )}

      <Card><CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Computo aperto</div>
          <h2 className="text-2xl font-bold mt-1" data-testid="computo-title">{computo.nome} <span className="text-sm text-slate-500 font-normal">v.{computo.versione}</span></h2>
          {computo.descrizione && <p className="text-sm text-slate-500 mt-1">{computo.descrizione}</p>}
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            {(() => {
              const SRC = { xlsx: "Import XLSX", xpwe: "Import XPWE", dcf: "Import DCF", xlsx_analisi: "Excel con analisi" };
              return <Badge variant="outline" className="capitalize">{SRC[computo.source_type] || "Manuale"}</Badge>;
            })()}
            <Badge variant="outline">{capitoli.length} capitoli</Badge>
            {computo.source_type !== "xlsx_analisi" && (<>
            <PercentualeListinoBadge computo={computo} isAdmin={isAdmin} onUpdated={(newComp) => setComputo(newComp)} />
            <MdoRulesBadge computo={computo} canEdit={canEdit} onApplied={(newComp) => setComputo(newComp)} />
            <MdoRulesCategorieBadge
              computo={computo}
              canEdit={canEdit}
              categorie={categorie}
              onApplied={() => loadCategorieBreakdown(computo.id)}
            />
            <InviluppoAutoBadge
              computo={computo}
              canEdit={canEdit}
              onApplied={() => loadCategorieBreakdown(computo.id)}
              autoOpen={searchParams.get("open_inviluppo_auto") === computo.id}
              onAutoOpened={() => {
                // Consuma il flag una sola volta: rimuove il query param dall'URL
                // così su refresh/apertura successiva non si riapre nulla.
                const sp = new URLSearchParams(searchParams);
                if (sp.has("open_inviluppo_auto")) {
                  sp.delete("open_inviluppo_auto");
                  setSearchParams(sp, { replace: true });
                }
              }}
            />
            <MaterialiRulesBadge
              computo={computo}
              canEdit={canEdit}
              onApplied={() => { loadCategorieBreakdown(computo.id); }}
            />
            <MaterialiRulesCategorieBadge
              computo={computo}
              canEdit={canEdit}
              categorie={categorie}
              onApplied={() => { loadCategorieBreakdown(computo.id); }}
            />
            </>)}
            <Badge className="bg-amber-500/20 text-amber-800 dark:text-amber-300 border-amber-500/40" data-testid="computo-totale-badge">€ {fmt(totaleComputo)}</Badge>
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setNewCapOpen(true)} data-testid="new-capitolo-btn"><FolderPlus className="h-4 w-4 mr-2" /> Nuovo capitolo</Button>
            {isAdmin && (<>
            <Button variant="outline" onClick={() => navigate(`/g/${groupId}/cantieri/${cantiere.id}/import`)} data-testid="import-xlsx-btn"><Upload className="h-4 w-4 mr-2" /> Importa XLSX</Button>
            <Button variant="outline" onClick={() => navigate(`/g/${groupId}/cantieri/${cantiere.id}/import-xpwe`)} data-testid="import-xpwe-btn"><FileCode2 className="h-4 w-4 mr-2" /> Importa XPWE</Button>
            </>)}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={!!exportingSezione} data-testid="export-dati-btn">
                  <Download className="h-4 w-4 mr-2" />
                  {exportingSezione ? "Esportazione…" : "Esporta dati"}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Esporta come XLSX</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => downloadExport("computo", "Solo computo")} data-testid="export-computo-solo">Solo computo</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport("materiali", "Materiali previsti")} data-testid="export-materiali">Materiali previsti</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport("ordini", "Ordini materiali")} data-testid="export-ordini">Ordini materiali</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport("mdo_ore", "Manodopera prevista — ore")} data-testid="export-mdo-ore">Manodopera prevista — ore</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport("mdo_quantita", "Manodopera prevista — quantità")} data-testid="export-mdo-quantita">Manodopera prevista — quantità</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport("xpwe", "Dati Computo / XPWE")} data-testid="export-xpwe">Dati Computo / XPWE</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => downloadExport("preventivo", "Preventivo")} className="text-sky-700 dark:text-sky-400" data-testid="export-preventivo">Preventivo</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport("preventivo_descrizione", "Preventivo + descrizione")} className="text-sky-700 dark:text-sky-400" data-testid="export-preventivo-descrizione">Preventivo + descrizione</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport("preventivo_descrizione_costi", "Preventivo + descrizione + costi")} className="text-sky-700 dark:text-sky-400" data-testid="export-preventivo-descrizione-costi">Preventivo + descrizione + costi</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setInvCostoTipo("totale"); setInvCostoModalOpen(true); }} className="text-sky-700 dark:text-sky-400" data-testid="export-preventivo-descrizione-costi-inviluppo">Preventivo + descrizione + costi INVILUPPO</DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport("preventivo_descrizione_costi_mdo_xpwe", "Preventivo + descrizione + costi MDO XPWE")} className="text-sky-700 dark:text-sky-400" data-testid="export-preventivo-descrizione-costi-mdo-xpwe">Preventivo + descrizione + costi MDO XPWE</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => downloadExport("tutto", "Tutto insieme")} className="font-semibold text-amber-700 dark:text-amber-400" data-testid="export-tutto">Tutto insieme (6 fogli)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </CardContent></Card>

      {capitoli.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-500">
          <p>Nessun capitolo. Aggiungi un capitolo manualmente o importa un file XLSX.</p>
        </CardContent></Card>
      ) : (
        <>
        {/* Riepilogo stato cantiere */}
        {summary && summary.total > 0 && (
          <Card className="mb-3" data-testid="stato-summary-card"><CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Stato lavorazioni · {summary.total} righe</span>
              {STATI_LAV.map(s => (
                <div key={s.key} className="flex items-center gap-1.5 text-xs" data-testid={`summary-${s.key}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${s.dotClass}`}></span>
                  <span className="font-medium">{s.label}</span>
                  <span className="font-mono text-slate-500">{summary.by_stato[s.key] || 0}</span>
                  <span className="text-slate-400">({summary.percent[s.key] || 0}%)</span>
                </div>
              ))}
            </div>
          </CardContent></Card>
        )}
        {/* Ricerca nel computo aperto */}
        <div className="flex items-center gap-2 mb-2" data-testid="search-container">
          <div className="relative flex-1 max-w-md">
            <FileSpreadsheet className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input
              type="search" value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Cerca codice o voce nel computo... (Invio per navigare)"
              className="h-8 pl-7 pr-8 text-xs"
              data-testid="search-input"
            />
            {searchQ && (
              <button
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xs"
                aria-label="Cancella ricerca" data-testid="search-clear"
              >✕</button>
            )}
          </div>
          {searchResults && (
            <span className="text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap" data-testid="search-count">
              {searchLoading
                ? "…"
                : (currentSearchIndex >= 0 && searchResults.total > 0
                    ? `${currentSearchIndex + 1} / ${searchResults.total} risultat${searchResults.total === 1 ? "o" : "i"}`
                    : `${searchResults.total} risultat${searchResults.total === 1 ? "o" : "i"}`)}
            </span>
          )}
        </div>
        {/* Filtri stato lavorazione */}
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <ListFilter className="h-3.5 w-3.5 text-slate-400" />
          <button onClick={() => setStatoFilter("all")} data-testid="stato-filter-all"
            className={`text-xs px-2 py-1 rounded ${statoFilter === "all" ? "bg-amber-500 text-slate-950 font-semibold" : "bg-slate-100 dark:bg-slate-800"}`}>Tutte</button>
          {STATI_LAV.map(s => (
            <button key={s.key} onClick={() => setStatoFilter(s.key)} data-testid={`stato-filter-${s.key}`}
              className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${statoFilter === s.key ? "bg-amber-500 text-slate-950 font-semibold" : "bg-slate-100 dark:bg-slate-800"}`}>
              <span className={`w-2 h-2 rounded-full ${s.dotClass}`}></span>{s.label}
            </button>
          ))}
        </div>

        {/* Categorie operative: riepilogo + filtro + classifica */}
        <CategorieBar
          computo={computo}
          canEdit={canEdit}
          categorie={categorie}
          categorieRiepilogo={categorieRiepilogo}
          catSubsSummary={catSubsSummary}
          categoriaFilter={categoriaFilter}
          onFilterChange={setCategoriaFilter}
          onOpenRevisione={() => setRevisioneOpen(true)}
          onOpenSubsAssegna={() => setSubsAssegnaOpen(true)}
          classifyRunning={classifyRunning}
          onClassifyRun={async () => {
            if (classifyRunning) return;
            setClassifyRunning(true);
            try {
              const { data } = await api.post(`/computi/${computo.id}/classifica-categorie`, { dry_run: false, only_unassigned: true });
              const nKw = data.classificate_keyword || 0;
              const nCtx = data.classificate_contesto || 0;
              const nDaClass = data.fallback_da_classificare || 0;
              const nManuali = data.righe_saltate_manuali || 0;
              toast.success(`Classificazione completata · ${nKw} keyword · ${nCtx} contesto · ${nDaClass} da classificare · ${nManuali} manuali saltate`);
              // Refresh dati: riepilogo + breakdown MDO + righe già caricate
              await Promise.all([loadCategorieRiepilogo(computo.id), loadCategorieBreakdown(computo.id)]);
              openCap.forEach(cid => loadRighe(cid, { offset: 0, append: false }));
            } catch (e) {
              toast.error(e.response?.data?.detail || e.message || "Errore classificazione");
            } finally { setClassifyRunning(false); }
          }}
        />

        {/* Dual view selector: XPWE (albero) vs Categorie operative (raggruppamento) */}
        <div className="flex items-center gap-1 mb-2" data-testid="vista-selector">
          <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mr-2">Vista</span>
          <button
            onClick={() => setViewMode("xpwe")}
            className={`text-xs px-3 py-1 rounded-l border ${viewMode === "xpwe" ? "bg-amber-500 text-slate-950 font-semibold border-amber-500" : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}
            data-testid="vista-xpwe-btn"
            title="Vista ad albero originale (capitoli XPWE)"
          >XPWE</button>
          <button
            onClick={() => setViewMode("categorie")}
            className={`text-xs px-3 py-1 rounded-r border-y border-r flex items-center gap-1 ${viewMode === "categorie" ? "bg-amber-500 text-slate-950 font-semibold border-amber-500" : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}
            data-testid="vista-categorie-btn"
            title="Raggruppa le stesse righe per Categoria Operativa"
          ><Tags className="h-3 w-3" /> Categorie operative</button>
          {isAdmin && (
            <Button size="sm" variant="outline" className="ml-3 h-7 text-[11px]" onClick={() => setGestCatOpen(true)} data-testid="apri-gestione-categorie">
              <Settings className="h-3 w-3 mr-1" /> Gestisci categorie
            </Button>
          )}
        </div>

        {viewMode === "xpwe" && (
        <Accordion type="multiple" value={openCap} onValueChange={setOpenCap} className="space-y-2">
          {capitoliDFS.map(cap => (
            <AccordionItem key={cap.id} value={cap.id}
              style={cap._depth ? { marginLeft: `${cap._depth * 20}px` } : undefined}
              className="border border-slate-200 dark:border-slate-800 rounded-lg bg-white dark:bg-slate-900" data-testid={`capitolo-item-${cap.id}`}>
              <AccordionTrigger className="px-4 py-3 hover:no-underline" onClick={() => { if (!righePerCap[cap.id]?.items?.length) loadRighe(cap.id); }}>
                <div className="flex-1 flex items-center justify-between pr-4">
                  <div className="text-left">
                    <div className="font-semibold flex items-center gap-2">
                      {cap._depth === 0 && <span className="text-[10px] uppercase tracking-widest font-bold text-amber-600">SUPER</span>}
                      {cap._depth === 1 && <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">CAT</span>}
                      {cap._depth >= 2 && <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">SUB</span>}
                      {cap.codice && <span className="text-xs font-mono text-slate-500">{cap.codice}</span>}
                      {cap.nome}
                      {cap.origin === "xpwe" && <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-600">XPWE</Badge>}
                      {cap.origin === "xlsx" && <Badge variant="outline" className="text-[10px]">XLSX</Badge>}
                      {cap.origin === "dcf" && <Badge variant="outline" className="text-[10px]">DCF</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{cap.righe_count} righe · Totale € {fmt(cap.importo_totale_capitolo)}</div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Righe del capitolo</div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openNewRiga(cap.id)} data-testid={`add-riga-${cap.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Riga</Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteCap(cap.id)} data-testid={`del-cap-${cap.id}`} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="text-left py-2 px-2 w-20">N.Ord</th>
                        <th className="text-left py-2 px-2 w-24">Tariffa</th>
                        <th className="text-left py-2 px-2">Designazione</th>
                        <th className="text-left py-2 px-2 w-16">UM</th>
                        <th className="text-right py-2 px-2 w-24">Q.tà</th>
                        <th className="text-right py-2 px-2 w-28">Prezzo un.</th>
                        <th className="text-right py-2 px-2 w-32">Importo</th>
                        <th className="text-left py-2 px-2 w-40">Stato</th>
                        <th className="w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderRigheRows(righePerCap[cap.id]?.items || [], {
                        onStatoChange: (id, st) => changeRigaStato(id, cap.id, st),
                        onDelete: (id) => deleteRiga(id, cap.id),
                      })}
                      {(righePerCap[cap.id]?.items || []).length === 0 && (
                        <tr><td colSpan={9} className="text-center py-6 text-slate-500">Nessuna riga. Aggiungi la prima riga o importa un XLSX.</td></tr>
                      )}
                    </tbody>
                  </table>
                  {righePerCap[cap.id] && righePerCap[cap.id].items.length < righePerCap[cap.id].total && (
                    <LoadMoreTrigger onIntersect={() => loadMoreRighe(cap.id)} label={`Caricate ${righePerCap[cap.id].items.length} di ${righePerCap[cap.id].total} righe`} testId={`load-more-${cap.id}`} />
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        )}

        {viewMode === "categorie" && (
          <>
          {(!categorieRiepilogo || (categorieRiepilogo.categorie || []).length === 0) ? (
            <Card><CardContent className="p-6 text-center text-sm text-slate-500" data-testid="vista-categorie-empty">
              Nessuna categoria disponibile. Esegui la classificazione dal pulsante sopra per raggruppare le righe.
            </CardContent></Card>
          ) : (
          <Accordion type="multiple" value={openCat} onValueChange={setOpenCat} className="space-y-2" data-testid="vista-categorie-accordion">
            {[
              // Categorie con almeno una riga (ordine di categorieRiepilogo, che ordina per numero_righe desc)
              ...(categorieRiepilogo.categorie || []).map(c => ({
                key: c.categoria_id, id: c.categoria_id, nome: c.nome, codice: c.codice, count: c.numero_righe, isNone: false,
              })),
              // Righe senza categoria (categoria_operativa_id IS NULL) — solo se presenti
              ...((categorieRiepilogo.senza_categoria || 0) > 0 ? [{
                key: "none", id: null, nome: "Senza categoria", codice: null, count: categorieRiepilogo.senza_categoria, isNone: true,
              }] : []),
            ].map(cat => (
              <AccordionItem key={cat.key} value={cat.key}
                className="border border-slate-200 dark:border-slate-800 rounded-lg bg-white dark:bg-slate-900" data-testid={`categoria-item-${cat.key}`}>
                <AccordionTrigger className="px-4 py-3 hover:no-underline" onClick={() => { if (!righePerCat[cat.key]?.items?.length) loadRigheCat(cat.key); }}>
                  <div className="flex-1 flex items-center justify-between pr-4">
                    <div className="text-left">
                      <div className="font-semibold flex items-center gap-2">
                        <Tags className={`h-3.5 w-3.5 ${cat.isNone ? "text-slate-400" : cat.codice === "DA_CLASSIFICARE" ? "text-amber-600" : "text-amber-500"}`} />
                        {cat.nome}
                        <Badge variant="outline" className="text-[10px]">{cat.count} righe</Badge>
                      </div>
                      {cat.codice && <div className="text-[10px] font-mono text-slate-500 mt-0.5">{cat.codice}</div>}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Righe della categoria</div>
                    {!cat.isNone && canEdit && (
                      <Button
                        size="sm" variant="outline"
                        disabled={exportingSezione === `cat-${cat.id}`}
                        onClick={() => downloadExport("tutto", `Categoria: ${cat.nome}`, cat.id)}
                        data-testid={`download-categoria-${cat.id}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1" />
                        {exportingSezione === `cat-${cat.id}` ? "Esportazione…" : "Scarica XLSX"}
                      </Button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
                        <tr>
                          <th className="text-left py-2 px-2 w-20">N.Ord</th>
                          <th className="text-left py-2 px-2 w-24">Tariffa</th>
                          <th className="text-left py-2 px-2">Designazione</th>
                          <th className="text-left py-2 px-2 w-16">UM</th>
                          <th className="text-right py-2 px-2 w-24">Q.tà</th>
                          <th className="text-right py-2 px-2 w-28">Prezzo un.</th>
                          <th className="text-right py-2 px-2 w-32">Importo</th>
                          <th className="text-left py-2 px-2 w-40">Stato</th>
                          <th className="w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderRigheRows(righePerCat[cat.key]?.items || [], {
                          onStatoChange: (id, st) => changeRigaStatoCat(id, cat.key, st),
                          onDelete: null,
                        })}
                        {(righePerCat[cat.key]?.items || []).length === 0 && (
                          <tr><td colSpan={9} className="text-center py-6 text-slate-500">
                            {righePerCat[cat.key] ? "Nessuna riga in questa categoria per i filtri correnti." : "Caricamento…"}
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                    {righePerCat[cat.key] && righePerCat[cat.key].items.length < righePerCat[cat.key].total && (
                      <LoadMoreTrigger onIntersect={() => loadMoreRigheCat(cat.key)} label={`Caricate ${righePerCat[cat.key].items.length} di ${righePerCat[cat.key].total} righe`} testId={`load-more-cat-${cat.key}`} />
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          )}
          </>
        )}
        </>
      )}

      {/* dialog nuovo capitolo */}
      <Dialog open={newCapOpen} onOpenChange={setNewCapOpen}>
        <DialogContent><DialogHeader><DialogTitle>Nuovo capitolo</DialogTitle><DialogDescription>Aggiungi un capitolo al computo attivo.</DialogDescription></DialogHeader>
          <form onSubmit={createCap} className="space-y-4">
            <div><Label>Nome capitolo</Label><Input required value={capForm.nome} onChange={(e) => setCapForm({ ...capForm, nome: e.target.value })} data-testid="cap-nome-input" /></div>
            <div><Label>Codice (opzionale)</Label><Input value={capForm.codice} onChange={(e) => setCapForm({ ...capForm, codice: e.target.value })} data-testid="cap-codice-input" /></div>
            <DialogFooter><Button type="submit" data-testid="cap-create-submit" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">Crea</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* dialog nuova riga */}
      <Dialog open={newRigaOpen} onOpenChange={setNewRigaOpen}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Nuova riga di computo</DialogTitle><DialogDescription>Compila i dati della lavorazione.</DialogDescription></DialogHeader>
          <form onSubmit={createRiga} className="grid grid-cols-2 gap-4">
            <div><Label>Num. Ord.</Label><Input value={rigaForm.num_ord} onChange={(e) => setRigaForm({ ...rigaForm, num_ord: e.target.value })} data-testid="riga-numord" /></div>
            <div><Label>Tariffa</Label><Input value={rigaForm.tariffa} onChange={(e) => setRigaForm({ ...rigaForm, tariffa: e.target.value })} data-testid="riga-tariffa" /></div>
            <div className="col-span-2"><Label>Designazione *</Label><Textarea required value={rigaForm.designazione} onChange={(e) => setRigaForm({ ...rigaForm, designazione: e.target.value })} data-testid="riga-designazione" /></div>
            <div><Label>Unità di misura</Label><Input value={rigaForm.unita_misura} onChange={(e) => setRigaForm({ ...rigaForm, unita_misura: e.target.value })} data-testid="riga-um" placeholder="mq / ml / n." /></div>
            <div><Label>Quantità</Label><Input type="number" step="0.0001" value={rigaForm.quantita} onChange={(e) => setRigaForm({ ...rigaForm, quantita: e.target.value })} data-testid="riga-quantita" /></div>
            <div><Label>Prezzo unitario</Label><Input type="number" step="0.0001" value={rigaForm.prezzo_unitario} onChange={(e) => setRigaForm({ ...rigaForm, prezzo_unitario: e.target.value })} data-testid="riga-prezzo" /></div>
            <div className="col-span-2 text-xs text-slate-500 flex items-center gap-1"><Sparkles className="h-3 w-3" /> L&apos;importo totale è calcolato automaticamente (quantità × prezzo).</div>
            <DialogFooter className="col-span-2"><Button type="submit" data-testid="riga-create-submit" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">Crea riga</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* dialog revisione rapida "Da classificare" */}
      {revisioneOpen && (
        <RevisioneRapidaDialog
          open={revisioneOpen}
          onClose={() => setRevisioneOpen(false)}
          computo={computo}
          categorie={categorie}
          categorieById={categorieById}
          onChanged={() => {
            // Refresh: riepilogo + righe correnti nei capitoli aperti
            loadCategorieRiepilogo(computo.id);
            openCap.forEach(cid => loadRighe(cid, { offset: 0, append: false }));
          }}
        />
      )}
      {subsAssegnaOpen && (
        <SubsAssegnazioneDialog
          open={subsAssegnaOpen}
          onClose={() => setSubsAssegnaOpen(false)}
          computo={computo}
          onChanged={() => loadCatSubsSummary(computo.id)}
        />
      )}
      {isAdmin && (
        <GestioneCategorieDialog
          open={gestCatOpen}
          onOpenChange={setGestCatOpen}
          categorie={categorie}
          onChanged={() => { loadCategorie(); if (computo?.id) loadCategorieRiepilogo(computo.id); }}
        />
      )}

      {/* Modal scelta tipo_costo per export "Preventivo + descrizione + costi INVILUPPO" */}
      <Dialog open={invCostoModalOpen} onOpenChange={setInvCostoModalOpen}>
        <DialogContent className="max-w-md" data-testid="inv-costo-modal">
          <DialogHeader>
            <DialogTitle>Esporta costi INVILUPPO</DialogTitle>
            <DialogDescription>Quale costo vuoi esportare?</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="flex items-center gap-3 cursor-pointer rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60">
              <input
                type="radio"
                name="inv-costo-tipo"
                value="mdo"
                checked={invCostoTipo === "mdo"}
                onChange={() => setInvCostoTipo("mdo")}
                data-testid="inv-costo-mdo"
              />
              <span>Manodopera</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60">
              <input
                type="radio"
                name="inv-costo-tipo"
                value="materiali"
                checked={invCostoTipo === "materiali"}
                onChange={() => setInvCostoTipo("materiali")}
                data-testid="inv-costo-materiali"
              />
              <span>Materiali</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60">
              <input
                type="radio"
                name="inv-costo-tipo"
                value="totale"
                checked={invCostoTipo === "totale"}
                onChange={() => setInvCostoTipo("totale")}
                data-testid="inv-costo-totale"
              />
              <span>Totale MDO + Materiali</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInvCostoModalOpen(false)} data-testid="inv-costo-cancel">Annulla</Button>
            <Button
              className="bg-sky-600 hover:bg-sky-700 text-white"
              disabled={!!exportingSezione}
              data-testid="inv-costo-download"
              onClick={async () => {
                setInvCostoModalOpen(false);
                const label = invCostoTipo === "mdo"
                  ? "Preventivo + descrizione — MANODOPERA INVILUPPO"
                  : invCostoTipo === "materiali"
                    ? "Preventivo + descrizione — MATERIALI INVILUPPO"
                    : "Preventivo + descrizione + costi INVILUPPO";
                await downloadExport(
                  "preventivo_descrizione_costi_inviluppo",
                  label,
                  null,
                  { tipo_costo: invCostoTipo },
                );
              }}
            >
              Scarica Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Percentuale listino badge (inline editor per ADMIN) ----------
function PercentualeListinoBadge({ computo, isAdmin, onUpdated }) {
  const perc = Number(computo?.percentuale_listino || 0);
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(String(perc));
  const [saving, setSaving] = useState(false);

  const sign = perc > 0 ? "+" : "";
  const label = `Percentuale listino: ${sign}${perc}%`;

  const save = async () => {
    setSaving(true);
    try {
      const parsed = Number(String(val).replace(",", "."));
      if (Number.isNaN(parsed)) { toast.error("Valore non valido"); setSaving(false); return; }
      const { data } = await api.put(`/computi/${computo.id}/percentuale-listino`, { percentuale: parsed });
      toast.success(`Percentuale listino aggiornata a ${parsed}%`);
      onUpdated?.(data);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento percentuale");
    } finally { setSaving(false); }
  };

  if (!isAdmin) {
    return (
      <Badge variant="outline" data-testid="computo-perc-listino-badge">{label}</Badge>
    );
  }
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(String(perc)); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-slate-300 dark:border-slate-700 text-xs hover:border-amber-500 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
          data-testid="computo-perc-listino-btn"
          title="Modifica percentuale listino (solo ADMIN)"
        >
          {label} <Pencil className="h-3 w-3 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56" align="start">
        <Label className="text-xs">Percentuale listino (%)</Label>
        <Input
          type="text" inputMode="decimal" value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="es. 6 · 0 · -5"
          className="mt-1" data-testid="computo-perc-listino-input"
        />
        <div className="mt-3 flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="computo-perc-listino-cancel">Annulla</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="computo-perc-listino-save"
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">
            {saving ? "…" : "Salva"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// Regole massive MDO da Computo/XPWE — pannello compatto in header computo.
// Salvataggio SOLO su "Applica regole". Anteprima live via preview endpoint.
function MdoRulesBadge({ computo, canEdit, onApplied }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [total, setTotal] = useState(null);
  const [touched, setTouched] = useState(0);
  const [saving, setSaving] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);

  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

  const loadState = async () => {
    try {
      const { data } = await api.get(`/computi/${computo.id}/mdo-rules`);
      const items = (data.rules || []).map((r, i) => ({
        _k: i, da_percent: r.da_percent, a_percent: r.a_percent,
        tipo: r.tipo, valore: r.valore, attivo: !!r.attivo,
      }));
      setRules(items);
      setTotal(data.mdo_stimata_totale);
      setTouched(data.righe_toccate || 0);
    } catch (e) { console.warn("mdo-rules load:", e?.message); }
  };
  useEffect(() => { loadState(); /* eslint-disable-line */ }, [computo.id]);

  // Anteprima live (debounced 300ms) via POST preview
  useEffect(() => {
    if (!open) return;
    const h = setTimeout(async () => {
      const payload = {
        rules: rules.map((r) => ({
          da_percent: Number(r.da_percent || 0),
          a_percent: Number(r.a_percent || 0),
          tipo: r.tipo, valore: Number(r.valore || 0), attivo: !!r.attivo,
        })),
      };
      try {
        const { data } = await api.post(`/computi/${computo.id}/mdo-rules/preview`, payload);
        setTotal(data.mdo_stimata_totale); setTouched(data.righe_toccate || 0);
        setPreviewErr(null);
      } catch (e) {
        setPreviewErr(e.response?.data?.detail || e.message);
      }
    }, 300);
    return () => clearTimeout(h);
  }, [rules, open, computo.id]);

  const addRow = () => {
    if (rules.length >= 10) { toast.error("Massimo 10 intervalli."); return; }
    setRules([...rules, { _k: Date.now(), da_percent: "0", a_percent: "100", tipo: "incremento", valore: "0", attivo: true }]);
  };
  const updRow = (idx, patch) => setRules(rules.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const delRow = (idx) => setRules(rules.filter((_, i) => i !== idx));

  const apply = async () => {
    setSaving(true);
    try {
      const payload = {
        rules: rules.map((r) => ({
          da_percent: Number(r.da_percent || 0),
          a_percent: Number(r.a_percent || 0),
          tipo: r.tipo, valore: Number(r.valore || 0), attivo: !!r.attivo,
        })),
      };
      const { data } = await api.post(`/computi/${computo.id}/mdo-rules/apply`, payload);
      setTotal(data.mdo_stimata_totale); setTouched(data.righe_toccate || 0);
      toast.success(`Regole applicate: ${data.righe_aggiornate} righe aggiornate${data.righe_saltate_inc0 ? ` · ${data.righe_saltate_inc0} saltate (inc=0)` : ""}`);
      setOpen(false);
      if (onApplied) onApplied({ ...computo, mdo_rules: data.rules });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore apply");
    } finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-800 dark:text-amber-300 hover:bg-amber-500/20"
          data-testid="mdo-rules-trigger"
          title="Regole massive MDO XPWE"
        >
          <Sparkles className="h-3 w-3" /> Regole MDO XPWE
          <span className="ml-1 font-mono">€ {fmt(total)}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[720px] max-w-[95vw] p-3" data-testid="mdo-rules-panel">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-xs font-semibold">Regole massive MDO da Computo / XPWE</div>
            <div className="text-[10px] text-slate-500">
              Anteprima live · <b className="font-mono">MDO stimata € {fmt(total)}</b>
              {touched > 0 && <span> · {touched} righe matchano gli intervalli</span>}
            </div>
          </div>
          {canEdit && <Button size="sm" variant="outline" onClick={addRow} disabled={rules.length >= 10} data-testid="mdo-rules-add"><Plus className="h-3 w-3 mr-1" />Intervallo</Button>}
        </div>
        {previewErr && <div className="text-[11px] text-red-600 mb-2">{previewErr}</div>}
        <div className="max-h-[50vh] overflow-auto">
          {rules.length === 0 && <div className="text-xs text-slate-500 italic py-4 text-center">Nessuna regola. Le righe useranno l'incremento manuale o quello automatico.</div>}
          {rules.map((r, idx) => (
            <div key={r._k} className="grid grid-cols-[64px_64px_1fr_80px_60px_28px] items-center gap-1 mb-1">
              <Input type="number" step="0.01" placeholder="Da %" value={r.da_percent} onChange={(e) => updRow(idx, { da_percent: e.target.value })} disabled={!canEdit} className="h-7 text-xs" data-testid={`mdo-rule-da-${idx}`} />
              <Input type="number" step="0.01" placeholder="A %" value={r.a_percent} onChange={(e) => updRow(idx, { a_percent: e.target.value })} disabled={!canEdit} className="h-7 text-xs" data-testid={`mdo-rule-a-${idx}`} />
              <select value={r.tipo} onChange={(e) => updRow(idx, { tipo: e.target.value })} disabled={!canEdit} className="h-7 text-xs rounded-md border border-slate-300 bg-transparent px-2" data-testid={`mdo-rule-tipo-${idx}`}>
                <option value="incremento">Incremento %</option>
                <option value="fisso">Fisso %</option>
                <option value="punti">+ punti %</option>
              </select>
              <Input type="number" step="0.01" placeholder="Valore" value={r.valore} onChange={(e) => updRow(idx, { valore: e.target.value })} disabled={!canEdit} className="h-7 text-xs" data-testid={`mdo-rule-val-${idx}`} />
              <label className="text-[10px] flex items-center justify-center gap-1"><input type="checkbox" checked={r.attivo} onChange={(e) => updRow(idx, { attivo: e.target.checked })} disabled={!canEdit} data-testid={`mdo-rule-attivo-${idx}`} /> attivo</label>
              <Button size="icon" variant="ghost" onClick={() => delRow(idx)} disabled={!canEdit} className="h-6 w-6" data-testid={`mdo-rule-del-${idx}`}><Trash2 className="h-3 w-3" /></Button>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-slate-200 dark:border-slate-700">
          <Button size="sm" variant="ghost" onClick={() => { setOpen(false); loadState(); }} data-testid="mdo-rules-cancel">Annulla</Button>
          <Button size="sm" onClick={apply} disabled={!canEdit || saving} data-testid="mdo-rules-apply">{saving ? "Applicazione…" : "Applica regole"}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ============================================================================
// Barra Categorie Operative: riepilogo classificazione + filtro + pulsante
// "Classifica lavorazioni". Compatta e mobile-friendly.
// ============================================================================
function CategorieBar({ computo, canEdit, categorie, categorieRiepilogo, catSubsSummary, categoriaFilter, onFilterChange, onOpenRevisione, onOpenSubsAssegna, classifyRunning, onClassifyRun }) {
  const totRighe = categorieRiepilogo?.totale_righe ?? null;
  const nClass = categorieRiepilogo?.classificate ?? null;
  const nDaClass = categorieRiepilogo?.da_classificare ?? 0;
  const daClassCat = categorie.find(c => c.codice === "DA_CLASSIFICARE");

  return (
    <div className="mb-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5 flex flex-wrap items-center gap-2" data-testid="categorie-bar">
      <div className="flex items-center gap-1.5 text-xs">
        <Tags className="h-3.5 w-3.5 text-slate-500" />
        <span className="uppercase tracking-widest text-slate-500 font-semibold">Categorie lavorazioni</span>
      </div>
      {categorieRiepilogo ? (
        <>
          <span className="text-xs text-slate-600 dark:text-slate-400" data-testid="categorie-riepilogo">
            <b className="font-mono">{nClass}</b> / {totRighe} classificate
          </span>
          {nDaClass > 0 && daClassCat && (
            <button
              type="button"
              onClick={() => onFilterChange(daClassCat.id)}
              className="text-[11px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/40 hover:bg-amber-500/30"
              data-testid="categorie-da-classificare-quick"
              title="Filtra le voci ancora da classificare"
            >
              Da classificare — {nDaClass} voci
            </button>
          )}
          {nDaClass > 0 && canEdit && (
            <button
              type="button"
              onClick={onOpenRevisione}
              className="text-[11px] px-2 py-0.5 rounded bg-sky-500/15 text-sky-800 dark:text-sky-300 border border-sky-500/40 hover:bg-sky-500/25 inline-flex items-center gap-1"
              data-testid="revisione-rapida-btn"
              title="Revisiona rapidamente le voci Da classificare"
            >
              <ListChecks className="h-3 w-3" /> Revisiona
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={onOpenSubsAssegna}
              className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25 inline-flex items-center gap-1"
              data-testid="subs-assegna-btn"
              title="Assegna Subappaltatori alle Categorie Operative del computo"
            >
              <Users2 className="h-3 w-3" /> Subappaltatori
              {catSubsSummary && (
                <span className="font-mono text-[10px] ml-1" data-testid="subs-summary-badge">
                  {catSubsSummary.assegnate}/{catSubsSummary.totali}
                </span>
              )}
            </button>
          )}
        </>
      ) : (
        <span className="text-[11px] text-slate-400 italic">caricamento…</span>
      )}
      {/* Filtro categoria */}
      <div className="ml-auto flex items-center gap-2">
        <label htmlFor="cat-filter" className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Categoria</label>
        <select
          id="cat-filter"
          value={categoriaFilter}
          onChange={(e) => onFilterChange(e.target.value)}
          className="h-7 text-xs rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 min-w-[160px]"
          data-testid="categoria-filter-select"
        >
          <option value="all">Tutte</option>
          <option value="none">Senza categoria</option>
          {categorie.map(c => (
            <option key={c.id} value={c.id}>{c.nome}</option>
          ))}
        </select>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={onClassifyRun}
            disabled={classifyRunning}
            className="h-7 px-2 text-xs"
            data-testid="classifica-btn"
            title="Classifica automaticamente le righe non ancora assegnate manualmente"
          >
            <Wand2 className="h-3 w-3 mr-1" />
            {classifyRunning ? "Classificazione…" : "Classifica lavorazioni"}
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// STEP B.3 — Pannello UNICO "MDO + Materiali XPWE" per Categoria Operativa.
// Un solo trigger, un solo pannello: riepilogo generale + per-categoria +
// regole MDO e Materiali affiancate (2 colonne desktop, stack mobile).
// ============================================================================
function MdoRulesCategorieBadge({ computo, canEdit, categorie, onApplied }) {
  const [open, setOpen] = useState(false);
  const [breakdown, setBreakdown] = useState(null); // MDO breakdown (per apply UX legacy)
  const [riepilogo, setRiepilogo] = useState(null); // STEP B.3 — costo-riepilogo aggregato
  const [rulesByCat, setRulesByCat] = useState({}); // MDO {cat_id: [rules]}
  const [mateRulesByCat, setMateRulesByCat] = useState({}); // Materiali XPWE {cat_id: [rules]}
  const [mateRulesGeneral, setMateRulesGeneral] = useState([]); // Materiali XPWE globali (read-only qui)
  const [openCatAccordion, setOpenCatAccordion] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingMate, setSavingMate] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMateConfirm, setShowMateConfirm] = useState(false);
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const fmtPct = (n) => (n == null ? "—" : `${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);

  const loadState = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get(`/computi/${computo.id}/mdo-rules-categorie`),
        api.get(`/computi/${computo.id}/materiali-xpwe-rules`),
        api.get(`/computi/${computo.id}/costo-riepilogo`),
      ]);
      setBreakdown(r1.data);
      // MDO rules_by_categoria
      const rebc = {};
      Object.entries(r1.data.rules_by_categoria || {}).forEach(([cid, rules]) => {
        rebc[cid] = (rules || []).map((r, i) => ({
          _k: `${cid}-${i}-${Date.now()}`, da_percent: r.da_percent, a_percent: r.a_percent,
          tipo: r.tipo, valore: r.valore, attivo: !!r.attivo,
        }));
      });
      setRulesByCat(rebc);
      // Materiali XPWE: globali + per-categoria
      setMateRulesGeneral((r2.data.rules || []).map((r, i) => ({
        _k: `mg-${i}-${Date.now()}`, da_percent: r.da_percent, a_percent: r.a_percent,
        tipo: r.tipo, valore: r.valore, attivo: !!r.attivo,
      })));
      const rbc = {};
      Object.entries(r2.data.rules_by_categoria || {}).forEach(([cid, rules]) => {
        rbc[cid] = (rules || []).map((r, i) => ({
          _k: `m-${cid}-${i}-${Date.now()}`, da_percent: r.da_percent, a_percent: r.a_percent,
          tipo: r.tipo, valore: r.valore, attivo: !!r.attivo,
        }));
      });
      setMateRulesByCat(rbc);
      // Riepilogo aggregato
      setRiepilogo(r3.data);
    } catch (e) { console.warn("mdo+mate load:", e?.message); }
  };

  const reloadRiepilogo = async () => {
    try {
      const { data } = await api.get(`/computi/${computo.id}/costo-riepilogo`);
      setRiepilogo(data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { if (open) loadState(); /* eslint-disable-line */ }, [open, computo.id]);

  // Preview live MDO (mantiene il comportamento esistente)
  useEffect(() => {
    if (!open) return;
    const h = setTimeout(async () => {
      const payload = {
        rules_by_categoria: Object.fromEntries(
          Object.entries(rulesByCat)
            .filter(([, arr]) => (arr || []).length > 0)
            .map(([cid, arr]) => [cid, arr.map(r => ({
              da_percent: Number(r.da_percent || 0),
              a_percent: Number(r.a_percent || 0),
              tipo: r.tipo, valore: Number(r.valore || 0), attivo: !!r.attivo,
            }))])
        ),
      };
      try {
        const { data } = await api.post(`/computi/${computo.id}/mdo-rules-categorie/preview`, payload);
        setBreakdown(data); setPreviewErr(null);
      } catch (e) {
        const d = e.response?.data?.detail;
        setPreviewErr(typeof d === "string" ? d : (d ? JSON.stringify(d) : e.message));
      }
    }, 300);
    return () => clearTimeout(h);
  }, [rulesByCat, open, computo.id]);

  // MDO rule helpers
  const addRule = (cid) => {
    setRulesByCat(prev => {
      const arr = prev[cid] || [];
      if (arr.length >= 10) { toast.error("Massimo 10 intervalli per categoria"); return prev; }
      return { ...prev, [cid]: [...arr, { _k: `${cid}-${Date.now()}`, da_percent: "0", a_percent: "100", tipo: "incremento", valore: "0", attivo: true }] };
    });
  };
  const updRule = (cid, idx, patch) => {
    setRulesByCat(prev => ({ ...prev, [cid]: (prev[cid] || []).map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  };
  const delRule = (cid, idx) => {
    setRulesByCat(prev => ({ ...prev, [cid]: (prev[cid] || []).filter((_, i) => i !== idx) }));
  };

  // Materiali rule helpers (speculari, ma su mateRulesByCat)
  const addMateRule = (cid) => {
    setMateRulesByCat(prev => {
      const arr = prev[cid] || [];
      if (arr.length >= 10) { toast.error("Massimo 10 intervalli Materiali per categoria"); return prev; }
      return { ...prev, [cid]: [...arr, { _k: `m-${cid}-${Date.now()}`, da_percent: "0", a_percent: "100", tipo: "incremento", valore: "0", attivo: true }] };
    });
  };
  const updMateRule = (cid, idx, patch) => {
    setMateRulesByCat(prev => ({ ...prev, [cid]: (prev[cid] || []).map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  };
  const delMateRule = (cid, idx) => {
    setMateRulesByCat(prev => ({ ...prev, [cid]: (prev[cid] || []).filter((_, i) => i !== idx) }));
  };

  // Save + Apply MDO (endpoint esistente)
  const doApply = async () => {
    setSaving(true);
    try {
      const payload = {
        rules_by_categoria: Object.fromEntries(
          Object.entries(rulesByCat)
            .filter(([, arr]) => (arr || []).length > 0)
            .map(([cid, arr]) => [cid, arr.map(r => ({
              da_percent: Number(r.da_percent || 0),
              a_percent: Number(r.a_percent || 0),
              tipo: r.tipo, valore: Number(r.valore || 0), attivo: !!r.attivo,
            }))])
        ),
      };
      const { data } = await api.post(`/computi/${computo.id}/mdo-rules-categorie/apply`, payload);
      const c = data.counters || {};
      // STEP B.4 — riepilogo con protezione manual + legacy
      toast.success(
        `MDO applicato · Applicate: ${c.righe_applicate ?? 0} · ` +
        `Manuali protette: ${c.righe_saltate_manual ?? 0} · ` +
        `Legacy protette: ${c.righe_saltate_legacy ?? 0} · ` +
        `Senza dato XPWE: ${c.righe_senza_dato ?? 0}`
      );
      setBreakdown(data);
      setShowConfirm(false);
      await reloadRiepilogo();
      if (onApplied) onApplied();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d ? JSON.stringify(d) : e.message));
    } finally { setSaving(false); }
  };

  // Save Materiali rules per-categoria (salva ogni categoria che ha regole) + apply globale.
  // Sequenza (semplice e coerente col backend STEP A):
  //  1. POST /materiali-xpwe-rules/categorie/{cat_id} per ogni categoria con regole
  //  2. DELETE per categorie che l'utente ha svuotato (avevamo regole, ora array vuoto)
  //  3. POST /materiali-xpwe-rules/apply con array vuoto (mantiene regole globali già salvate)
  //     per rifare l'apply massivo sulle righe (categoria > globale > 0%).
  const doApplyMate = async () => {
    setSavingMate(true);
    try {
      // 1) salvataggio per-categoria (solo categorie con almeno 1 regola)
      const savedCatIds = new Set();
      for (const [cid, arr] of Object.entries(mateRulesByCat)) {
        if (!Array.isArray(arr) || arr.length === 0) continue;
        const rules = arr.map(r => ({
          da_percent: Number(r.da_percent || 0),
          a_percent: Number(r.a_percent || 0),
          tipo: r.tipo, valore: Number(r.valore || 0), attivo: !!r.attivo,
        }));
        await api.post(`/computi/${computo.id}/materiali-xpwe-rules/categorie/${cid}`, { rules });
        savedCatIds.add(cid);
      }
      // 2) DELETE per categorie vuotate (categorie mai avute non serve)
      for (const cid of Object.keys(mateRulesByCat)) {
        if (savedCatIds.has(cid)) continue;
        // se c'era rule salvata e ora è vuota → delete
        try { await api.delete(`/computi/${computo.id}/materiali-xpwe-rules/categorie/${cid}`); } catch { /* noop */ }
      }
      // 3) apply massivo con regole globali salvate (usa quelle già presenti)
      const globPayload = { rules: mateRulesGeneral.map(r => ({
        da_percent: Number(r.da_percent || 0),
        a_percent: Number(r.a_percent || 0),
        tipo: r.tipo, valore: Number(r.valore || 0), attivo: !!r.attivo,
      })) };
      const { data } = await api.post(`/computi/${computo.id}/materiali-xpwe-rules/apply`, globPayload);
      toast.success(
        `Materiali applicati · Applicate: ${data.righe_applicate ?? 0} · ` +
        `Manuali protette: ${data.righe_saltate_manual ?? 0} · ` +
        `Legacy protette: ${data.righe_saltate_legacy ?? 0} · ` +
        `Senza dato XPWE: ${data.righe_senza_dato ?? 0}`
      );
      setShowMateConfirm(false);
      await reloadRiepilogo();
      if (onApplied) onApplied();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d ? JSON.stringify(d) : e.message));
    } finally { setSavingMate(false); }
  };

  // Merge dati riepilogo per categoria (nome, importo, MDO, Materiali, margine)
  const rieMap = {};
  (riepilogo?.per_categoria || []).forEach(c => {
    rieMap[c.categoria_id || "none"] = c;
  });

  // Preferisci mostrare categorie con almeno una riga; se non ci sono
  // dati, mostra tutte le categorie attive.
  const perCat = breakdown?.per_categoria || [];
  const categoriesToRender = (() => {
    if (!perCat.length && !(riepilogo?.per_categoria || []).length) return categorie;
    const withRows = perCat.filter(p => p.categoria_id && p.righe_considerate > 0);
    const withRowsIds = new Set(withRows.map(p => p.categoria_id));
    const withRulesIds = Object.keys(rulesByCat).filter(cid => (rulesByCat[cid] || []).length > 0);
    const extraFromRules = categorie.filter(c => !withRowsIds.has(c.id) && withRulesIds.includes(c.id));
    const seen = new Set(withRows.map(p => p.categoria_id));
    const rest = categorie.filter(c => !seen.has(c.id) && !extraFromRules.find(x => x.id === c.id));
    return [
      ...withRows.map(p => ({
        id: p.categoria_id, nome: p.nome, codice: p.codice,
        _righe: p.righe_considerate, _importo: p.importo_lavorazioni,
        _mdo_orig: p.mdo_xpwe_originale_euro, _mdo_fin: p.mdo_finale_euro,
        _diff: p.differenza_euro, _inc_final: p.incidenza_finale_percent,
      })),
      ...extraFromRules,
      ...rest,
    ];
  })();

  const totale = breakdown?.totale;
  const rieT = riepilogo?.totale;

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-xs font-medium text-sky-800 dark:text-sky-300 hover:bg-sky-500/20"
          data-testid="mdo-cat-trigger"
          title="Regole MDO + Materiali XPWE per Categoria Operativa"
        >
          <Sparkles className="h-3 w-3" /> MDO + Materiali
          {rieT?.totale_previsto && <span className="ml-1 font-mono">€ {fmt(rieT.totale_previsto)}</span>}
          <ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom" align="end" sideOffset={6}
        avoidCollisions collisionPadding={16}
        className="w-[980px] max-w-[97vw] p-3 overflow-y-auto"
        style={{ maxHeight: "var(--radix-popover-content-available-height, 85vh)" }}
        data-testid="mdo-cat-panel"
      >
        {/* --- HEADER + RIEPILOGO GENERALE --- */}
        <div className="mb-3">
          <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
            <div>
              <div className="text-xs font-semibold">Regole MDO + Materiali XPWE per Categoria Operativa</div>
              <div className="text-[10px] text-slate-500 leading-tight">
                Priorità: <b>Categoria</b> → <b>Regole generali</b> → <b>XPWE originale</b>. Mai sommate.
                MDO e Materiali sono <b>separati</b> — <i>MaterialiPrevisti</i> (Analisi Prezzi) NON entrano.
              </div>
            </div>
          </div>
          {rieT && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-slate-50/70 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-md p-2 text-[11px]" data-testid="riepilogo-computo">
              <div>
                <div className="uppercase tracking-widest text-slate-500 text-[9px]">Importo computo</div>
                <div className="font-mono font-semibold" data-testid="rie-importo-computo">€ {fmt(rieT.importo_computo)}</div>
                <div className="text-[9px] text-slate-500">{rieT.numero_voci} voci</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-sky-600 text-[9px]">MDO INVILUPPO</div>
                <div className="font-mono font-semibold" data-testid="rie-mdo-inv">€ {fmt(rieT.mdo_inviluppo)}</div>
                <div className="text-[9px] text-slate-500">orig · € {fmt(rieT.mdo_originale_xpwe)} ({fmtPct(rieT.mdo_originale_percent)})</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-emerald-600 text-[9px]">MATERIALI INVILUPPO</div>
                <div className="font-mono font-semibold" data-testid="rie-mate-inv">€ {fmt(rieT.materiali_inviluppo)}</div>
                <div className="text-[9px] text-slate-500">orig · € {fmt(rieT.materiali_originali_xpwe)} ({fmtPct(rieT.materiali_originali_percent)})</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-amber-600 text-[9px]">MARGINE RESIDUO</div>
                <div className={`font-mono font-semibold ${Number(rieT.margine_residuo) < 0 ? 'text-rose-600' : ''}`} data-testid="rie-margine">€ {fmt(rieT.margine_residuo)}</div>
                <div className="text-[9px] text-slate-500">
                  {fmtPct(rieT.margine_residuo_percent)} · totale previsto € {fmt(rieT.totale_previsto)}
                </div>
              </div>
              {rieT.voci_senza_inc_mdo > 0 && (
                <div className="col-span-2 md:col-span-4 mt-1 text-[10px] text-amber-700 dark:text-amber-400" data-testid="rie-voci-senza">
                  ⚠ {rieT.voci_senza_inc_mdo} voci senza dato MDO XPWE non coperte da MDO/Materiali.
                </div>
              )}
            </div>
          )}
        </div>
        {previewErr && <div className="text-[11px] text-red-600 mb-2 border border-red-200 bg-red-50 dark:bg-red-950/30 rounded px-2 py-1" data-testid="mdo-cat-preview-err">{previewErr}</div>}

        {/* --- CATEGORIE --- */}
        <Accordion type="multiple" value={openCatAccordion} onValueChange={setOpenCatAccordion} className="space-y-1.5">
          {categoriesToRender.map(cat => {
            const rules = rulesByCat[cat.id] || [];
            const mateRules = mateRulesByCat[cat.id] || [];
            const hasRules = rules.length > 0;
            const hasMateRules = mateRules.length > 0;
            const rie = rieMap[cat.id];
            return (
              <AccordionItem key={cat.id} value={cat.id} className="border border-slate-200 dark:border-slate-800 rounded-md bg-slate-50/40 dark:bg-slate-900/40" data-testid={`mdo-cat-item-${cat.id}`}>
                <AccordionTrigger className="px-3 py-2 hover:no-underline">
                  <div className="flex-1 flex items-center justify-between pr-2 gap-2 flex-wrap">
                    <div className="text-left flex items-center gap-2 flex-wrap min-w-0">
                      <Tags className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                      <span className="text-sm font-semibold truncate">{cat.nome}</span>
                      {hasRules && <Badge variant="outline" className="text-[10px] border-sky-500 text-sky-700 dark:text-sky-300">MDO {rules.length}</Badge>}
                      {hasMateRules && <Badge variant="outline" className="text-[10px] border-emerald-500 text-emerald-700 dark:text-emerald-300">Mat {mateRules.length}</Badge>}
                      {rie && <span className="text-[11px] text-slate-500">{rie.numero_voci} voci</span>}
                    </div>
                    {rie && (
                      <div className="text-[10px] text-slate-500 font-mono text-right whitespace-nowrap">
                        <div>Imp · € {fmt(rie.importo_computo)}</div>
                        <div>
                          MDO · <span className="text-sky-700 dark:text-sky-300">€ {fmt(rie.mdo_inviluppo)}</span> ({fmtPct(rie.mdo_inviluppo_percent)})
                          {" · "}
                          Mat · <span className="text-emerald-700 dark:text-emerald-300">€ {fmt(rie.materiali_inviluppo)}</span> ({fmtPct(rie.materiali_inviluppo_percent)})
                        </div>
                        <div>
                          Totale · € {fmt(rie.totale_previsto)}
                          {" · "}
                          <span className={`${Number(rie.margine_residuo) < 0 ? 'text-rose-600' : 'text-amber-700 dark:text-amber-300'}`}>Margine · € {fmt(rie.margine_residuo)} ({fmtPct(rie.margine_residuo_percent)})</span>
                        </div>
                        {rie.voci_senza_inc_mdo > 0 && (
                          <div className="text-amber-600 text-[9px]">⚠ {rie.voci_senza_inc_mdo} senza dato MDO XPWE</div>
                        )}
                      </div>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  {/* --- COLONNE MDO | MATERIALI (2 col desktop, stack mobile) --- */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* --- MDO --- */}
                    <div className="border border-sky-500/30 rounded-md p-2 bg-sky-50/30 dark:bg-sky-950/10">
                      <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-700 dark:text-sky-300 mb-1">MANODOPERA</div>
                      {rie && (
                        <div className="text-[10px] font-mono text-slate-600 dark:text-slate-400 mb-2 space-y-0.5">
                          <div>Orig · € {fmt(rie.mdo_originale_xpwe)} ({fmtPct(rie.mdo_originale_percent)})</div>
                          <div>INVILUPPO · <b>€ {fmt(rie.mdo_inviluppo)}</b> ({fmtPct(rie.mdo_inviluppo_percent)})</div>
                          <div>Diff · € {fmt(Number(rie.mdo_inviluppo) - Number(rie.mdo_originale_xpwe))}</div>
                        </div>
                      )}
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Regole MDO</div>
                      {rules.length === 0 && (
                        <div className="text-[11px] text-slate-500 italic py-1">Nessuna regola MDO. La categoria eredita regole generali o MDO XPWE originale.</div>
                      )}
                      <div className="overflow-x-auto">
                        {rules.map((r, idx) => (
                          <div key={r._k} className="grid grid-cols-[54px_54px_1fr_70px_50px_24px] items-center gap-1 mb-1 min-w-[360px]">
                            <Input type="number" step="0.01" placeholder="Da %" value={r.da_percent} onChange={(e) => updRule(cat.id, idx, { da_percent: e.target.value })} disabled={!canEdit} className="h-7 text-xs" data-testid={`mdo-cat-da-${cat.id}-${idx}`} />
                            <Input type="number" step="0.01" placeholder="A %" value={r.a_percent} onChange={(e) => updRule(cat.id, idx, { a_percent: e.target.value })} disabled={!canEdit} className="h-7 text-xs" data-testid={`mdo-cat-a-${cat.id}-${idx}`} />
                            <select value={r.tipo} onChange={(e) => updRule(cat.id, idx, { tipo: e.target.value })} disabled={!canEdit} className="h-7 text-xs rounded-md border border-slate-300 bg-transparent px-2" data-testid={`mdo-cat-tipo-${cat.id}-${idx}`}>
                              <option value="incremento">Incremento %</option>
                              <option value="fisso">Fisso %</option>
                              <option value="punti">+ punti %</option>
                            </select>
                            <Input type="number" step="0.01" placeholder="Valore" value={r.valore} onChange={(e) => updRule(cat.id, idx, { valore: e.target.value })} disabled={!canEdit} className="h-7 text-xs" data-testid={`mdo-cat-val-${cat.id}-${idx}`} />
                            <label className="text-[10px] flex items-center justify-center gap-1"><input type="checkbox" checked={r.attivo} onChange={(e) => updRule(cat.id, idx, { attivo: e.target.checked })} disabled={!canEdit} data-testid={`mdo-cat-attivo-${cat.id}-${idx}`} /> attivo</label>
                            <Button size="icon" variant="ghost" onClick={() => delRule(cat.id, idx)} disabled={!canEdit} className="h-6 w-6" data-testid={`mdo-cat-del-${cat.id}-${idx}`}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        ))}
                      </div>
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => addRule(cat.id)} disabled={rules.length >= 10} className="mt-1 h-7 text-xs" data-testid={`mdo-cat-add-${cat.id}`}>
                          <Plus className="h-3 w-3 mr-1" /> Intervallo MDO
                        </Button>
                      )}
                    </div>

                    {/* --- MATERIALI XPWE --- */}
                    <div className="border border-emerald-500/30 rounded-md p-2 bg-emerald-50/30 dark:bg-emerald-950/10">
                      <div className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-1">MATERIALI XPWE</div>
                      {rie && (
                        <div className="text-[10px] font-mono text-slate-600 dark:text-slate-400 mb-2 space-y-0.5">
                          <div>Orig · € {fmt(rie.materiali_originali_xpwe)} ({fmtPct(rie.materiali_originali_percent)})</div>
                          <div>INVILUPPO · <b>€ {fmt(rie.materiali_inviluppo)}</b> ({fmtPct(rie.materiali_inviluppo_percent)})</div>
                          <div>Diff · € {fmt(Number(rie.materiali_inviluppo) - Number(rie.materiali_originali_xpwe))}</div>
                        </div>
                      )}
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Regole Materiali (su quota_materiale = 100 − IncMDO)</div>
                      {mateRules.length === 0 && (
                        <div className="text-[11px] text-slate-500 italic py-1">Nessuna regola Materiali. La categoria eredita regole globali (se presenti) o 0%.</div>
                      )}
                      <div className="overflow-x-auto">
                        {mateRules.map((r, idx) => (
                          <div key={r._k} className="grid grid-cols-[54px_54px_1fr_70px_50px_24px] items-center gap-1 mb-1 min-w-[360px]">
                            <Input type="number" step="0.01" placeholder="Da %" value={r.da_percent} onChange={(e) => updMateRule(cat.id, idx, { da_percent: e.target.value })} disabled={!canEdit} className="h-7 text-xs" data-testid={`mate-cat-da-${cat.id}-${idx}`} />
                            <Input type="number" step="0.01" placeholder="A %" value={r.a_percent} onChange={(e) => updMateRule(cat.id, idx, { a_percent: e.target.value })} disabled={!canEdit} className="h-7 text-xs" data-testid={`mate-cat-a-${cat.id}-${idx}`} />
                            <select value={r.tipo} onChange={(e) => updMateRule(cat.id, idx, { tipo: e.target.value })} disabled={!canEdit} className="h-7 text-xs rounded-md border border-slate-300 bg-transparent px-2" data-testid={`mate-cat-tipo-${cat.id}-${idx}`}>
                              <option value="incremento">Incremento %</option>
                              <option value="fisso">Fisso %</option>
                              <option value="punti">+ punti %</option>
                            </select>
                            <Input type="number" step="0.01" placeholder="Valore" value={r.valore} onChange={(e) => updMateRule(cat.id, idx, { valore: e.target.value })} disabled={!canEdit} className="h-7 text-xs" data-testid={`mate-cat-val-${cat.id}-${idx}`} />
                            <label className="text-[10px] flex items-center justify-center gap-1"><input type="checkbox" checked={r.attivo} onChange={(e) => updMateRule(cat.id, idx, { attivo: e.target.checked })} disabled={!canEdit} data-testid={`mate-cat-attivo-${cat.id}-${idx}`} /> attivo</label>
                            <Button size="icon" variant="ghost" onClick={() => delMateRule(cat.id, idx)} disabled={!canEdit} className="h-6 w-6" data-testid={`mate-cat-del-${cat.id}-${idx}`}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        ))}
                      </div>
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => addMateRule(cat.id)} disabled={mateRules.length >= 10} className="mt-1 h-7 text-xs" data-testid={`mate-cat-add-${cat.id}`}>
                          <Plus className="h-3 w-3 mr-1" /> Intervallo Materiali
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* --- Riepilogo mini in fondo categoria --- */}
                  {rie && (
                    <div className="mt-3 pt-2 border-t border-slate-200 dark:border-slate-700 text-[11px] font-mono grid grid-cols-2 md:grid-cols-5 gap-2">
                      <div>Importo · <b>€ {fmt(rie.importo_computo)}</b></div>
                      <div className="text-sky-700 dark:text-sky-300">MDO · <b>€ {fmt(rie.mdo_inviluppo)}</b></div>
                      <div className="text-emerald-700 dark:text-emerald-300">Materiali · <b>€ {fmt(rie.materiali_inviluppo)}</b></div>
                      <div>Totale · <b>€ {fmt(rie.totale_previsto)}</b></div>
                      <div className={`${Number(rie.margine_residuo) < 0 ? 'text-rose-600' : 'text-amber-700 dark:text-amber-300'}`}>Margine · <b>€ {fmt(rie.margine_residuo)}</b> ({fmtPct(rie.margine_residuo_percent)})</div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {/* --- FOOTER: Apply MDO / Apply Materiali separati per chiarezza --- */}
        <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-slate-200 dark:border-slate-700 flex-wrap">
          <Button size="sm" variant="ghost" onClick={() => { setOpen(false); }} data-testid="mdo-cat-cancel">Annulla</Button>
          <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" onClick={() => setShowMateConfirm(true)} disabled={!canEdit || savingMate} data-testid="mate-cat-apply">
            {savingMate ? "Applicazione Materiali…" : "Applica regole Materiali"}
          </Button>
          <Button size="sm" onClick={() => setShowConfirm(true)} disabled={!canEdit || saving} data-testid="mdo-cat-apply">
            {saving ? "Applicazione MDO…" : "Applica regole MDO"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>

    {/* Dialog conferma MDO (comportamento esistente) */}
    <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Applicare le regole MDO per categoria?</DialogTitle>
          <DialogDescription>
            Verranno ricalcolati gli incrementi MDO delle righe secondo la priorità:
            <br />
            <b>Categoria → Regole generali → XPWE originale</b>.
            <br /><br />
            <span className="text-emerald-700 dark:text-emerald-400"><b>Le modifiche manuali delle singole voci non verranno sovrascritte.</b></span>
            <br />
            <span className="text-slate-600 dark:text-slate-400 text-[11px]">Le righe con valore MDO preesistente (legacy, senza provenance registrata) vengono anch'esse protette per sicurezza. Per applicare la regola su una di queste righe: apri la singola voce, Reset e ri-esegui l'apply.</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setShowConfirm(false)} data-testid="mdo-cat-confirm-cancel">Annulla</Button>
          <Button onClick={doApply} disabled={saving} data-testid="mdo-cat-confirm-ok">{saving ? "Applicazione…" : "Applica"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Dialog conferma Materiali (avviso override manuali) */}
    <Dialog open={showMateConfirm} onOpenChange={setShowMateConfirm}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Applicare le regole Materiali XPWE?</DialogTitle>
          <DialogDescription>
            Verranno salvate le regole Materiali per categoria e applicate alle righe secondo:
            <br />
            <b>Categoria → Globali → 0%</b>.
            <br /><br />
            <span className="text-emerald-700 dark:text-emerald-400"><b>Le modifiche manuali delle singole voci non verranno sovrascritte.</b></span>
            <br />
            <span className="text-slate-600 dark:text-slate-400 text-[11px]">Le righe con valore Materiali preesistente (legacy, senza provenance registrata) vengono anch'esse protette per sicurezza. Per applicare la regola su una di queste righe: apri la singola voce, Reset e ri-esegui l'apply.</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setShowMateConfirm(false)} data-testid="mate-cat-confirm-cancel">Annulla</Button>
          <Button onClick={doApplyMate} disabled={savingMate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mate-cat-confirm-ok">{savingMate ? "Applicazione…" : "Applica"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ============================================================================
// Revisione Rapida: elenco solo delle righe DA_CLASSIFICARE con select categoria
// inline. Mobile-friendly (layout verticale). Riusa API esistenti:
//  - GET /api/computi/{id}/righe/search?categoria_operativa_id=<DA_CLASSIFICARE>
//  - PATCH /api/computi/{computo_id}/righe/{riga_id}/categoria
// ============================================================================
function RevisioneRapidaDialog({ open, onClose, computo, categorie, categorieById, onChanged }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [savingByRow, setSavingByRow] = useState({}); // {riga_id: true}
  const [contextOpen, setContextOpen] = useState({}); // {riga_id: true} -> mostra contesto
  const [capitoliMap, setCapitoliMap] = useState({}); // {cap_id: capitolo}
  const daClassCat = categorie.find(c => c.codice === "DA_CLASSIFICARE");

  const load = async () => {
    if (!daClassCat) return;
    setLoading(true);
    try {
      // Paginazione automatica: l'endpoint search restituisce `total`. Carichiamo
      // a blocchi da 200 finché items.length < total. Safety cap: 50 pagine
      // (10.000 righe) per evitare loop patologici.
      const PAGE = 200;
      const MAX_PAGES = 50;
      let acc = [];
      let offset = 0;
      let grandTotal = 0;
      for (let i = 0; i < MAX_PAGES; i++) {
        const { data } = await api.get(`/computi/${computo.id}/righe/search`, {
          params: { categoria_operativa_id: daClassCat.id, limit: PAGE, offset },
        });
        const pageItems = data.items || [];
        grandTotal = data.total || 0;
        acc = acc.concat(pageItems);
        offset += pageItems.length;
        if (pageItems.length < PAGE) break; // fine naturale
        if (acc.length >= grandTotal) break; // raggiunto il totale
      }
      setItems(acc);
      setTotal(grandTotal);
      if (grandTotal > acc.length) {
        // ha superato MAX_PAGES: segnaliamo (raro, safety)
        toast.warning(`Caricate ${acc.length}/${grandTotal} voci — limite di sicurezza raggiunto`);
      }
      // Carica capitoli una sola volta per contesto
      if (!Object.keys(capitoliMap).length) {
        try {
          const capsRes = await api.get(`/computi/${computo.id}/capitoli`);
          const m = {};
          (capsRes.data || []).forEach(c => { m[c.id] = c; });
          setCapitoliMap(m);
        } catch {/* noop */}
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-line */ }, [open, computo?.id]);

  const assign = async (riga, catId) => {
    if (!catId) return;
    setSavingByRow(prev => ({ ...prev, [riga.id]: true }));
    try {
      await api.patch(`/computi/${computo.id}/righe/${riga.id}/categoria`, { categoria_operativa_id: catId });
      // Rimuove dalla lista locale + aggiorna contatore
      setItems(prev => prev.filter(r => r.id !== riga.id));
      setTotal(prev => Math.max(0, prev - 1));
      toast.success(`Assegnata · ${categorieById[catId]?.nome || "categoria"}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore assegnazione");
    } finally {
      setSavingByRow(prev => { const n = { ...prev }; delete n[riga.id]; return n; });
    }
  };

  // Ordinamento voci per capitolo → contesto (righe vicine dello stesso cap)
  // Deriva prev/next categoria dalla lista TOTALE del computo (usiamo `items`
  // per esempi vicini "Da classificare"; per prev/next classificati mostriamo
  // il capitolo/parent — non serve extra query).
  const capName = (capId) => capitoliMap[capId]?.nome || "";
  const parentName = (capId) => {
    const cap = capitoliMap[capId];
    if (!cap?.parent_id) return null;
    return capitoliMap[cap.parent_id]?.nome || null;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-4" data-testid="revisione-rapida-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-sky-600" />
            Revisione rapida · Da classificare
          </DialogTitle>
          <DialogDescription>
            Assegna una <b>Categoria Operativa</b> alle voci rimaste. Ogni salvataggio protegge la riga dal classificatore automatico (manuale).
            <br />
            <span className="text-[11px] text-slate-500">Le categorie rappresentano il <b>pacchetto operativo/subappalto</b>, non necessariamente il tipo merceologico puro.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-slate-600 dark:text-slate-400 mb-2" data-testid="revisione-count">
          {loading ? "Caricamento…" : (
            total === 0
              ? <b className="text-emerald-600">Tutte le voci sono state classificate! 🎉</b>
              : <>Rimangono <b className="font-mono">{total}</b> voci da revisionare</>
          )}
        </div>
        <div className="space-y-2">
          {items.map(r => {
            const saving = !!savingByRow[r.id];
            const showCtx = !!contextOpen[r.id];
            return (
              <div key={r.id} className="border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-50/50 dark:bg-slate-900/50" data-testid={`revisione-row-${r.id}`}>
                <div className="flex flex-wrap items-baseline gap-2 mb-1">
                  {r.tariffa && <span className="font-mono text-xs text-slate-500">{r.tariffa}</span>}
                  {r.num_ord && <span className="font-mono text-[11px] text-slate-400">N.Ord {r.num_ord}</span>}
                </div>
                <div className="text-sm mb-2">{r.designazione}</div>
                {(capName(r.capitolo_id) || parentName(r.capitolo_id)) && (
                  <div className="text-[11px] text-slate-500 mb-2">
                    <span className="uppercase tracking-widest font-semibold">Capitolo · </span>
                    {parentName(r.capitolo_id) && <span>{parentName(r.capitolo_id)} → </span>}
                    <span>{capName(r.capitolo_id) || "—"}</span>
                    <button
                      type="button"
                      onClick={() => setContextOpen(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                      className="ml-2 text-sky-600 dark:text-sky-400 underline text-[10px]"
                      data-testid={`revisione-toggle-context-${r.id}`}
                    >
                      {showCtx ? "Nascondi contesto" : "Mostra contesto"}
                    </button>
                  </div>
                )}
                {showCtx && (
                  <div className="mb-2 text-[11px] text-slate-500 bg-white/60 dark:bg-slate-900/60 rounded p-2 border border-slate-200 dark:border-slate-700">
                    <div className="font-mono">Capitolo ID: {r.capitolo_id}</div>
                    <div>Ordine riga: {r.ordine ?? "—"}</div>
                    <div>UM: {r.unita_misura || "—"} · Q.tà: {r.quantita ?? "—"} · Prezzo un.: € {r.prezzo_unitario ?? "—"}</div>
                    {r.imported_data?.inc_mdo_percent != null && (
                      <div>Incidenza MDO XPWE originale: {r.imported_data.inc_mdo_percent}%</div>
                    )}
                    <div className="italic mt-1">Suggerimento: apri il capitolo nel computo per vedere le righe vicine e capire il pacchetto operativo.</div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Categoria</label>
                  <select
                    disabled={saving}
                    defaultValue=""
                    onChange={(e) => assign(r, e.target.value)}
                    className="h-8 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 flex-1 min-w-0"
                    data-testid={`revisione-select-${r.id}`}
                  >
                    <option value="" disabled>— Scegli categoria —</option>
                    {categorie.filter(c => c.codice !== "DA_CLASSIFICARE").map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                  {saving && <span className="text-[11px] text-slate-400 italic">salvataggio…</span>}
                </div>
              </div>
            );
          })}
        </div>
        {!loading && total > 0 && items.length < total && (
          <div className="text-xs text-slate-400 italic text-center mt-2">
            Mostrate {items.length} di {total}. Aumenta il limite o riprova se necessario.
          </div>
        )}
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} data-testid="revisione-close">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Assegnazione Categoria Operativa ↔ Subappaltatore (Task 4A frontend).
// Riusa GET/PUT /computi/{id}/categorie-subappaltatori. Mostra SOLO categorie
// con righe nel computo. DA_CLASSIFICARE indicato ma non assegnabile.
// ============================================================================
function SubsAssegnazioneDialog({ open, onClose, computo, onChanged }) {
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingByCat, setSavingByCat] = useState({}); // {cat_id: true}
  const [copyDialog, setCopyDialog] = useState(null); // {cat, sub}

  const load = async () => {
    setLoading(true);
    try {
      const [dCats, dSubs] = await Promise.all([
        api.get(`/computi/${computo.id}/categorie-subappaltatori`),
        api.get(`/anagrafiche/subappaltatori`),
      ]);
      setCats(dCats.data?.categorie || []);
      // filtra archiviati e ordina per nome
      const active = (dSubs.data || []).filter(s => !s.is_archived);
      active.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
      setSubs(active);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-line */ }, [open, computo?.id]);

  const setAssignment = async (cat, subIds) => {
    setSavingByCat(prev => ({ ...prev, [cat.categoria_id]: true }));
    try {
      await api.put(`/computi/${computo.id}/categorie-subappaltatori`, {
        assegnazioni: [{ categoria_operativa_id: cat.categoria_id, subappaltatore_ids: subIds }],
      });
      // aggiornamento locale
      setCats(prev => prev.map(c => c.categoria_id === cat.categoria_id ? {
        ...c,
        subappaltatori: subIds.map(sid => {
          const s = subs.find(x => x.id === sid);
          return s ? { id: s.id, nome: s.nome, ragione_sociale: s.ragione_sociale, is_archived: false } : { id: sid, nome: "(?)" };
        }),
      } : c));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore salvataggio");
    } finally {
      setSavingByCat(prev => { const n = { ...prev }; delete n[cat.categoria_id]; return n; });
    }
  };

  const addSub = (cat, subId) => {
    if (!subId) return;
    const existingIds = (cat.subappaltatori || []).map(s => s.id);
    if (existingIds.includes(subId)) return;
    setAssignment(cat, [...existingIds, subId]);
  };
  const removeSub = (cat, subId) => {
    const existingIds = (cat.subappaltatori || []).map(s => s.id);
    setAssignment(cat, existingIds.filter(id => id !== subId));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-4" data-testid="subs-assegna-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users2 className="h-5 w-5 text-emerald-600" /> Subappaltatori per Categoria Operativa
          </DialogTitle>
          <DialogDescription>
            Definisci <b>chi eseguirà ciascun pacchetto operativo</b> in questo computo. Una categoria può avere anche più subappaltatori. Le modifiche vengono salvate immediatamente.
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="text-xs text-slate-400 italic">Caricamento…</div>}
        <div className="space-y-2 mt-2">
          {cats.map(cat => {
            const saving = !!savingByCat[cat.categoria_id];
            const existingIds = new Set((cat.subappaltatori || []).map(s => s.id));
            const optionsAvailable = subs.filter(s => !existingIds.has(s.id));
            if (!cat.assegnabile) {
              return (
                <div key={cat.categoria_id} className="border border-amber-200 dark:border-amber-800/40 rounded-md p-3 bg-amber-50/40 dark:bg-amber-950/20 opacity-80" data-testid={`subs-cat-${cat.categoria_id}`}>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <Tags className="h-3.5 w-3.5 text-amber-700" /> {cat.nome}
                    <span className="text-[11px] uppercase tracking-widest font-normal text-amber-700">non assegnabile</span>
                  </div>
                  <div className="text-[11px] text-amber-700 dark:text-amber-400">{cat.numero_righe} voci · classifica prima le righe per assegnare</div>
                </div>
              );
            }
            return (
              <div key={cat.categoria_id} className="border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-50/50 dark:bg-slate-900/50" data-testid={`subs-cat-${cat.categoria_id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <Tags className="h-3.5 w-3.5 text-slate-500" /> {cat.nome}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">{cat.numero_righe} voci · € {Number(cat.importo_lavorazioni || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  {(cat.subappaltatori || []).length === 0 && (
                    <span className="text-[11px] text-slate-400 italic">Nessun subappaltatore assegnato</span>
                  )}
                  {(cat.subappaltatori || []).map(s => (
                    <span key={s.id} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border border-emerald-500/40" data-testid={`subs-chip-${cat.categoria_id}-${s.id}`}>
                      {s.nome}
                      <button type="button" onClick={() => setCopyDialog({ cat, sub: s })} className="inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-500/20 text-sky-800 dark:text-sky-300 border border-sky-500/40 hover:bg-sky-500/40" title="Copia MDO INVILUPPO in Manodopera prevista" data-testid={`subs-chip-copy-${cat.categoria_id}-${s.id}`}>
                        <Copy className="h-3 w-3" /> MDO
                      </button>
                      <button type="button" onClick={() => removeSub(cat, s.id)} disabled={saving} className="hover:text-red-600 ml-0.5" title="Rimuovi" data-testid={`subs-chip-remove-${cat.categoria_id}-${s.id}`}>
                        <XIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    defaultValue=""
                    disabled={saving || optionsAvailable.length === 0}
                    onChange={(e) => { if (e.target.value) { addSub(cat, e.target.value); e.target.value = ""; } }}
                    className="h-8 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 flex-1 min-w-0"
                    data-testid={`subs-add-select-${cat.categoria_id}`}
                  >
                    <option value="">{optionsAvailable.length === 0 ? "— tutti i subappaltatori già assegnati —" : "+ Aggiungi subappaltatore…"}</option>
                    {optionsAvailable.map(s => (
                      <option key={s.id} value={s.id}>{s.nome}{s.ragione_sociale ? ` · ${s.ragione_sociale}` : ""}</option>
                    ))}
                  </select>
                  {saving && <span className="text-[11px] text-slate-400 italic">salvataggio…</span>}
                </div>
              </div>
            );
          })}
          {!loading && cats.length === 0 && (
            <div className="text-xs text-slate-400 italic text-center py-4">Nessuna categoria classificata nel computo.</div>
          )}
        </div>
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} data-testid="subs-assegna-close">Chiudi</Button>
        </DialogFooter>
        {copyDialog && (
          <CopyMdoDialog
            open={!!copyDialog}
            onClose={() => setCopyDialog(null)}
            computo={computo}
            cat={copyDialog.cat}
            sub={copyDialog.sub}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------
// Copia MDO INVILUPPO → Manodopera prevista A quantità (singolo sub/cat).
// Riusa endpoint POST /computi/{id}/categorie/{cat_id}/copia-mdo-in-manodopera
// con dry_run per preview + update_existing checkbox.
// ------------------------------------------------------------
function CopyMdoDialog({ open, onClose, computo, cat, sub }) {
  const [preview, setPreview] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.post(`/computi/${computo.id}/categorie/${cat.categoria_id}/copia-mdo-in-manodopera`, {
        subappaltatore_id: sub.id, dry_run: true, update_existing: false,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore preview");
      onClose();
    }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-line */ }, [open]);

  const doApply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/computi/${computo.id}/categorie/${cat.categoria_id}/copia-mdo-in-manodopera`, {
        subappaltatore_id: sub.id, dry_run: false, update_existing: updateExisting,
      });
      setResult(data);
      toast.success(`Copia completata · create=${data.create} · aggiornate=${data.aggiornate} · saltate=${data.saltate}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore copia");
    } finally { setApplying(false); }
  };

  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="copy-mdo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-sky-600" /> Copia MDO INVILUPPO → Manodopera prevista
          </DialogTitle>
          <DialogDescription>
            <b>{cat.nome}</b> · Subappaltatore: <b>{sub.nome}</b>
            <br />
            Verranno create <b>Manodopera prevista A quantità</b> con prezzo €/UM = MDO INVILUPPO del computo.
            Il MDO del computo <b>non cambia</b>; se in futuro cambi le regole MDO, dovrai rilanciare questa copia manualmente per aggiornare.
          </DialogDescription>
        </DialogHeader>
        {!preview && <div className="text-xs text-slate-400 italic">Calcolo anteprima…</div>}
        {preview && !result && (
          <>
            <div className="text-xs text-slate-600 dark:text-slate-400 my-2 flex flex-wrap gap-3" data-testid="copy-mdo-summary">
              <span>Totale righe: <b>{preview.totale_righe_categoria}</b></span>
              <span>Pronte: <b className="text-emerald-600">{preview.righe_pronte}</b></span>
              <span>Già presenti: <b className="text-amber-600">{preview.gia_presenti}</b></span>
              <span>Saltate: <b className="text-slate-500">{preview.saltate}</b></span>
            </div>
            <div className="max-h-[45vh] overflow-y-auto border border-slate-200 dark:border-slate-800 rounded">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Tariffa</th>
                    <th className="text-left px-2 py-1">Designazione</th>
                    <th className="text-right px-2 py-1">Q.tà</th>
                    <th className="text-left px-2 py-1">UM</th>
                    <th className="text-right px-2 py-1">Prezzo voce</th>
                    <th className="text-right px-2 py-1">MDO XPWE</th>
                    <th className="text-right px-2 py-1">MDO INVILUPPO €/UM</th>
                    <th className="text-left px-2 py-1">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map(p => (
                    <tr key={p.riga_id} className="border-t border-slate-200 dark:border-slate-800">
                      <td className="px-2 py-1 font-mono">{p.tariffa}</td>
                      <td className="px-2 py-1 max-w-md truncate" title={p.designazione}>{p.designazione}</td>
                      <td className="px-2 py-1 text-right font-mono">{p.quantita ? fmt(p.quantita) : "—"}</td>
                      <td className="px-2 py-1">{p.um || "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{p.prezzo_voce ? "€ " + fmt(p.prezzo_voce) : "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{p.inc_mdo_percent ? fmt(p.inc_mdo_percent) + "%" : "—"}</td>
                      <td className="px-2 py-1 text-right font-mono font-semibold">{p.mdo_finale_um ? "€ " + fmt(p.mdo_finale_um) : "—"}</td>
                      <td className={`px-2 py-1 ${p.stato === "pronta" ? "text-emerald-600" : p.stato === "gia_presente" ? "text-amber-600" : "text-slate-400"}`}>
                        {p.stato === "pronta" ? "Pronta" : p.stato === "gia_presente" ? "Già presente" : "Saltata"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="flex items-center gap-2 mt-3 text-xs">
              <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} data-testid="copy-mdo-update-existing" />
              Aggiorna anche le Manodopera prevista già esistenti per questo subappaltatore
            </label>
            <DialogFooter className="mt-3">
              <Button variant="ghost" onClick={onClose} data-testid="copy-mdo-cancel">Annulla</Button>
              <Button onClick={doApply} disabled={applying || (preview.righe_pronte === 0 && !updateExisting)} data-testid="copy-mdo-apply">
                {applying ? "Copia in corso…" : "Copia in Manodopera prevista"}
              </Button>
            </DialogFooter>
          </>
        )}
        {result && (
          <div className="mt-3">
            <div className="rounded border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm" data-testid="copy-mdo-result">
              <div><b>Copia completata</b></div>
              <div>Create: <b>{result.create}</b></div>
              <div>Aggiornate: <b>{result.aggiornate}</b></div>
              <div>Già presenti non toccate: <b>{result.gia_presenti_non_toccate}</b></div>
              <div>Saltate: <b>{result.saltate}</b></div>
              <div>Errori: <b>{result.errori}</b></div>
            </div>
            <DialogFooter className="mt-3">
              <Button onClick={onClose} data-testid="copy-mdo-close">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
// GestioneCategorieDialog — Rinomina / Nuova / Unisci (Merge).
// Riusa CRUD esistenti: POST /categorie-operative, PATCH .../:id, POST .../merge.
// Nessun endpoint duplicato. Solo ADMIN (guardia lato UI + backend).
// =====================================================================
function GestioneCategorieDialog({ open, onOpenChange, categorie, onChanged }) {
  const active = (categorie || []).filter(c => c.is_active);
  // Rinomina
  const [renameId, setRenameId] = useState("");
  const [renameNome, setRenameNome] = useState("");
  // Nuova
  const [newCodice, setNewCodice] = useState("");
  const [newNome, setNewNome] = useState("");
  // Unisci
  const [mergeSrc, setMergeSrc] = useState("");
  const [mergeDst, setMergeDst] = useState("");
  const [mergeForce, setMergeForce] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (renameId) {
      const c = active.find(x => x.id === renameId);
      setRenameNome(c?.nome || "");
    } else setRenameNome("");
  }, [renameId]); // eslint-disable-line

  const doRename = async () => {
    if (!renameId || !renameNome.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/categorie-operative/${renameId}`, { nome: renameNome.trim() });
      toast.success("Categoria rinominata");
      onChanged && onChanged();
      setRenameId(""); setRenameNome("");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore rinomina"); }
    finally { setBusy(false); }
  };

  const doCreate = async () => {
    if (!newCodice.trim() || !newNome.trim()) return;
    setBusy(true);
    try {
      await api.post("/categorie-operative", { codice: newCodice.trim().toUpperCase(), nome: newNome.trim() });
      toast.success("Categoria creata");
      onChanged && onChanged();
      setNewCodice(""); setNewNome("");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
    finally { setBusy(false); }
  };

  const doMerge = async () => {
    if (!mergeSrc || !mergeDst) return;
    if (mergeSrc === mergeDst) { toast.error("Origine e destinazione devono essere diverse"); return; }
    if (!confirm(`Unire "${active.find(c=>c.id===mergeSrc)?.nome}" in "${active.find(c=>c.id===mergeDst)?.nome}"?\nL'origine verrà DISATTIVATA (non cancellata). Tutte le righe e i subappaltatori assegnati verranno spostati.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/categorie-operative/merge", {
        origine_id: mergeSrc, destinazione_id: mergeDst, force_mdo_rules: mergeForce
      });
      toast.success(`Unite: ${data.righe_spostate} righe, ${data.subappaltatori_spostati} sub spostati, ${data.subappaltatori_duplicati_scartati} duplicati scartati`);
      onChanged && onChanged();
      setMergeSrc(""); setMergeDst(""); setMergeForce(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "object" && d?.message) toast.error(`${d.message} · Computi in conflitto: ${(d.computi_in_conflitto||[]).length}`);
      else toast.error(d || "Errore merge");
    }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="gestione-categorie-dialog">
        <DialogHeader>
          <DialogTitle>Gestione Categorie Operative</DialogTitle>
          <DialogDescription>Rinomina, crea o unisci categorie operative. Le assegnazioni delle righe restano preservate.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 max-h-[70vh] overflow-y-auto">
          {/* RINOMINA */}
          <section className="space-y-2 p-3 border rounded-lg" data-testid="sez-rinomina">
            <div className="flex items-center gap-2 text-sm font-semibold"><Pencil className="h-3.5 w-3.5" /> Rinomina categoria</div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
              <select className="text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900" value={renameId} onChange={e => setRenameId(e.target.value)} data-testid="rename-select">
                <option value="">— seleziona —</option>
                {active.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
              <Input placeholder="Nuovo nome" value={renameNome} onChange={e => setRenameNome(e.target.value)} disabled={!renameId} data-testid="rename-input" />
              <Button onClick={doRename} disabled={busy || !renameId || !renameNome.trim()} data-testid="rename-btn">Rinomina</Button>
            </div>
            <p className="text-[10px] text-slate-500">L'ID non cambia. Le righe assegnate restano assegnate.</p>
          </section>

          {/* NUOVA */}
          <section className="space-y-2 p-3 border rounded-lg" data-testid="sez-nuova">
            <div className="flex items-center gap-2 text-sm font-semibold"><Plus className="h-3.5 w-3.5" /> Nuova categoria</div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
              <Input placeholder="CODICE (es. NUOVA_CAT)" value={newCodice} onChange={e => setNewCodice(e.target.value.toUpperCase())} data-testid="new-codice" />
              <Input placeholder="Nome visualizzato" value={newNome} onChange={e => setNewNome(e.target.value)} data-testid="new-nome" />
              <Button onClick={doCreate} disabled={busy || !newCodice.trim() || !newNome.trim()} data-testid="new-btn">Crea</Button>
            </div>
          </section>

          {/* MERGE */}
          <section className="space-y-2 p-3 border rounded-lg" data-testid="sez-merge">
            <div className="flex items-center gap-2 text-sm font-semibold"><Merge className="h-3.5 w-3.5" /> Unisci due categorie</div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-center">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Origine (verrà disattivata)</div>
                <select className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900" value={mergeSrc} onChange={e => setMergeSrc(e.target.value)} data-testid="merge-src">
                  <option value="">— seleziona —</option>
                  {active.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              <div className="text-center text-slate-400 text-xl">→</div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Destinazione (rimane attiva)</div>
                <select className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900" value={mergeDst} onChange={e => setMergeDst(e.target.value)} data-testid="merge-dst">
                  <option value="">— seleziona —</option>
                  {active.filter(c => c.id !== mergeSrc).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-400">
              <input type="checkbox" checked={mergeForce} onChange={e => setMergeForce(e.target.checked)} data-testid="merge-force" />
              In caso di conflitto MDO Rules: forza usando le regole della destinazione (default: blocca).
            </label>
            <Button variant="default" onClick={doMerge} disabled={busy || !mergeSrc || !mergeDst} data-testid="merge-btn">
              <Merge className="h-3.5 w-3.5 mr-1" /> Unisci
            </Button>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// FASE 2C — Regole MATERIALI (globali)
// Parallelo grafico a MdoRulesBadge, ma completamente separato.
// Endpoint (FASE 2B, backend inalterato):
//   GET  /computi/{id}/materiali-rules
//   POST /computi/{id}/materiali-rules            (SALVA regole in JSONB)
//   POST /computi/{id}/materiali-rules/preview    (STATELESS con body override)
//   POST /computi/{id}/materiali-rules/apply      (usa regole già salvate)
// Nota: `apply` NON accetta body → il flusso è Salva → Apply.
// La preview è ESPLICITA (nessuna live) per rispettare "Deve essere evidente
// che è una ANTEPRIMA".
// ============================================================================
export function MaterialiRulesBadge({ computo, canEdit, onApplied }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [previewData, setPreviewData] = useState(null); // {per_categoria, totale, warnings}
  const [previewErr, setPreviewErr] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

  const loadRules = async () => {
    try {
      const { data } = await api.get(`/computi/${computo.id}/materiali-rules`);
      const items = (data.rules || []).map((r, i) => ({
        _k: `${i}-${Date.now()}`,
        tipo: r.tipo || "incremento",
        valore: r.valore ?? "0",
        da_percent: r.da_percent ?? "",
        a_percent: r.a_percent ?? "",
        attivo: r.attivo !== false,
      }));
      setRules(items);
      setPreviewData(null); // preview azzerata dopo un load
    } catch (e) { console.warn("materiali-rules load:", e?.message); }
  };
  useEffect(() => { if (open) loadRules(); /* eslint-disable-line */ }, [open, computo.id]);

  const buildPayload = () => ({
    rules: rules.map(r => ({
      tipo: r.tipo,
      valore: Number(r.valore || 0),
      ...(r.da_percent !== "" && r.da_percent != null ? { da_percent: Number(r.da_percent) } : {}),
      ...(r.a_percent !== "" && r.a_percent != null ? { a_percent: Number(r.a_percent) } : {}),
      attivo: !!r.attivo,
    })),
  });

  const runPreview = async () => {
    setPreviewing(true); setPreviewErr(null);
    try {
      const { data } = await api.post(`/computi/${computo.id}/materiali-rules/preview`, buildPayload());
      setPreviewData(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setPreviewErr(typeof d === "string" ? d : (d ? JSON.stringify(d) : e.message));
    } finally { setPreviewing(false); }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      // 1) SALVA le regole (apply usa lo stato salvato)
      await api.post(`/computi/${computo.id}/materiali-rules`, buildPayload());
      // 2) APPLICA
      const { data } = await api.post(`/computi/${computo.id}/materiali-rules/apply`);
      toast.success(`Regole MATERIALI applicate · righe processate: ${data.righe_processate || 0} · materiali aggiornati: ${data.materiali_aggiornati || 0}`);
      setShowConfirm(false); setOpen(false);
      setPreviewData(null);
      if (onApplied) onApplied();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d ? JSON.stringify(d) : (e.message || "Errore apply")));
    } finally { setApplying(false); }
  };

  const addRule = () => {
    if (rules.length >= 10) { toast.error("Massimo 10 regole."); return; }
    setRules([...rules, { _k: Date.now(), tipo: "incremento", valore: "0", da_percent: "", a_percent: "", attivo: true }]);
    setPreviewData(null);
  };
  const updRule = (idx, patch) => { setRules(rules.map((r, i) => i === idx ? { ...r, ...patch } : r)); setPreviewData(null); };
  const delRule = (idx) => { setRules(rules.filter((_, i) => i !== idx)); setPreviewData(null); };

  const tot = previewData?.totale || null;
  const inc_orig_pct = tot && Number(tot.importo_lavorazioni) > 0
    ? (Number(tot.materiali_originali) / Number(tot.importo_lavorazioni) * 100) : null;
  const inc_inv_pct = tot && Number(tot.importo_lavorazioni) > 0
    ? (Number(tot.materiali_inviluppo) / Number(tot.importo_lavorazioni) * 100) : null;
  const diff_eur = tot ? (Number(tot.materiali_inviluppo) - Number(tot.materiali_originali)) : null;
  const diff_pct = tot && Number(tot.materiali_originali) > 0
    ? ((Number(tot.materiali_inviluppo) - Number(tot.materiali_originali)) / Number(tot.materiali_originali) * 100) : null;
  const righe_int = (previewData?.per_categoria || []).reduce((s, p) => s + Number(p.righe || 0), 0);

  const RULE_HINTS = {
    incremento: "Modifica proporzionalmente l'incidenza materiali originale.",
    fisso: "Imposta direttamente l'incidenza materiali sul prezzo della lavorazione.",
    punti: "Aggiunge o sottrae punti percentuali all'incidenza materiali originale.",
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-800 dark:text-emerald-300 hover:bg-emerald-500/20"
            data-testid="materiali-rules-trigger"
            title="Regole Materiali INVILUPPO"
          >
            <Sparkles className="h-3 w-3" /> Regole Materiali
            {rules.length > 0 && <Badge variant="outline" className="ml-1 text-[9px] border-emerald-500/60 text-emerald-700 dark:text-emerald-300">{rules.length}</Badge>}
            <ChevronDown className="h-3 w-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[760px] max-w-[95vw] p-3 max-h-[80vh] overflow-y-auto" data-testid="materiali-rules-panel">
          <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
            <div>
              <div className="text-xs font-semibold">Regole massive MATERIALI INVILUPPO</div>
              <div className="text-[10px] text-slate-500 leading-tight">
                Priorità: <b>Categoria</b> → <b>Regole globali</b> → <b>Materiali Analisi (originale)</b>. Mai sommate.
              </div>
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={addRule} disabled={rules.length >= 10} data-testid="materiali-rules-add">
                <Plus className="h-3 w-3 mr-1" /> Regola
              </Button>
            )}
          </div>

          {rules.length === 0 && (
            <div className="text-xs text-slate-500 italic py-4 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-md mb-2">
              Nessuna regola. I materiali INVILUPPO restano uguali all'Analisi Prezzi.
            </div>
          )}
          {rules.map((r, idx) => (
            <div key={r._k} className="border border-slate-200 dark:border-slate-800 rounded-md p-2 mb-2 bg-slate-50/50 dark:bg-slate-900/40">
              <div className="grid grid-cols-[1fr_100px_28px] gap-2 items-center">
                <select
                  value={r.tipo}
                  onChange={(e) => updRule(idx, { tipo: e.target.value })}
                  disabled={!canEdit}
                  className="h-8 text-xs rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2"
                  data-testid={`materiali-rule-tipo-${idx}`}
                >
                  <option value="incremento">Incremento %</option>
                  <option value="fisso">Fisso %</option>
                  <option value="punti">+ Punti %</option>
                </select>
                <Input type="number" step="0.01" placeholder="Valore" value={r.valore}
                       onChange={(e) => updRule(idx, { valore: e.target.value })}
                       disabled={!canEdit} className="h-8 text-xs"
                       data-testid={`materiali-rule-val-${idx}`} />
                <Button size="icon" variant="ghost" onClick={() => delRule(idx)} disabled={!canEdit} className="h-7 w-7" data-testid={`materiali-rule-del-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                </Button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1 italic">{RULE_HINTS[r.tipo]}</div>
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center mt-2 text-[11px]">
                <div className="flex items-center gap-1">
                  <span className="text-slate-500 shrink-0">Da % inc. orig.</span>
                  <Input type="number" step="0.01" placeholder="—" value={r.da_percent}
                         onChange={(e) => updRule(idx, { da_percent: e.target.value })}
                         disabled={!canEdit} className="h-7 text-xs"
                         data-testid={`materiali-rule-da-${idx}`} />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500 shrink-0">a %</span>
                  <Input type="number" step="0.01" placeholder="—" value={r.a_percent}
                         onChange={(e) => updRule(idx, { a_percent: e.target.value })}
                         disabled={!canEdit} className="h-7 text-xs"
                         data-testid={`materiali-rule-a-${idx}`} />
                </div>
                <label className="text-[10px] flex items-center gap-1 whitespace-nowrap">
                  <input type="checkbox" checked={r.attivo}
                         onChange={(e) => updRule(idx, { attivo: e.target.checked })}
                         disabled={!canEdit}
                         data-testid={`materiali-rule-attivo-${idx}`} /> attivo
                </label>
              </div>
            </div>
          ))}

          {/* Anteprima */}
          <div className="flex items-center gap-2 pt-2 mt-2 border-t border-slate-200 dark:border-slate-700">
            <Button size="sm" variant="outline" onClick={runPreview} disabled={previewing} data-testid="materiali-rules-preview-btn">
              {previewing ? "Anteprima…" : "Anteprima"}
            </Button>
            <span className="text-[10px] text-slate-500 italic">
              L'anteprima non applica nessuna modifica ai materiali.
            </span>
          </div>

          {previewErr && (
            <div className="text-[11px] text-red-600 mt-2 border border-red-200 bg-red-50 dark:bg-red-950/30 rounded px-2 py-1" data-testid="materiali-rules-preview-err">
              {previewErr}
            </div>
          )}

          {previewData && (
            <div className="mt-2 rounded-md border border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-950/20 p-2" data-testid="materiali-rules-preview-box">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-semibold mb-1">Anteprima (non applicata)</div>
              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
                <div>Materiali Analisi · <b>€ {fmt(tot?.materiali_originali)}</b></div>
                <div>Materiali INVILUPPO · <b>€ {fmt(tot?.materiali_inviluppo)}</b></div>
                <div>Incidenza Analisi · <b>{inc_orig_pct == null ? "—" : fmt(inc_orig_pct)}%</b></div>
                <div>Incidenza INVILUPPO · <b>{inc_inv_pct == null ? "—" : fmt(inc_inv_pct)}%</b></div>
                <div>Δ € · <b className={diff_eur > 0 ? "text-rose-600" : diff_eur < 0 ? "text-emerald-600" : ""}>€ {fmt(diff_eur)}</b></div>
                <div>Δ % · <b className={diff_pct > 0 ? "text-rose-600" : diff_pct < 0 ? "text-emerald-600" : ""}>{diff_pct == null ? "—" : fmt(diff_pct)}%</b></div>
                <div className="col-span-2 text-slate-500">Righe con materiali generati: <b>{righe_int}</b></div>
              </div>
              {(previewData.warnings || []).length > 0 && (
                <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-400">
                  {previewData.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-slate-200 dark:border-slate-700">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="materiali-rules-cancel">Chiudi</Button>
            <Button size="sm" onClick={() => setShowConfirm(true)} disabled={!canEdit || applying}
                    className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold"
                    data-testid="materiali-rules-apply">
              {applying ? "Applicazione…" : "Applica regole Materiali"}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md" data-testid="materiali-rules-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Applicare le regole MATERIALI?</DialogTitle>
            <DialogDescription>
              I prezzi INVILUPPO dei soli materiali generati da Analisi Prezzi verranno ricalcolati secondo le regole (priorità <b>Categoria → Globale → Analisi originale</b>).
              <br /><br />
              I materiali <b>manuali</b> non vengono toccati. Le quantità dei materiali non cambiano.
              <br />
              I prezzi originali dell'Analisi restano salvati e ripristinabili.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConfirm(false)} data-testid="materiali-rules-confirm-cancel">Annulla</Button>
            <Button onClick={runApply} disabled={applying} data-testid="materiali-rules-confirm-ok"
                    className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
              {applying ? "Applicazione…" : "Conferma e applica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ============================================================================
// FASE 2C — Regole MATERIALI per Categoria Operativa + Riepilogo
// Endpoint per-cat: GET/POST/DELETE /computi/{id}/materiali-rules/categorie/{cat}
// Preview aggregato: POST /materiali-rules/preview  (usa regole SALVATE)
// Applicazione: /materiali-rules/apply  (globale, non per singola categoria)
// Vincoli backend documentati:
//  - il preview endpoint accetta SOLO override delle regole globali; per
//    verificare l'effetto di una nuova regola di categoria è necessario
//    salvarla prima (POST /categorie/{cat}) e poi chiamare preview.
//  - nessun endpoint bulk: si esegue GET per ciascuna categoria.
// ============================================================================
export function MaterialiRulesCategorieBadge({ computo, canEdit, categorie, onApplied }) {
  const [open, setOpen] = useState(false);
  const [rulesByCat, setRulesByCat] = useState({}); // {cat_id: [rules]}
  const [breakdown, setBreakdown] = useState(null); // {per_categoria, totale, warnings}
  const [loading, setLoading] = useState(false);
  const [openAcc, setOpenAcc] = useState([]);
  const [savingCat, setSavingCat] = useState({});
  const [applying, setApplying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

  const loadAll = async () => {
    setLoading(true);
    try {
      // 1) fetch regole per ciascuna categoria (in parallelo)
      const results = await Promise.all(
        (categorie || []).map(c =>
          api.get(`/computi/${computo.id}/materiali-rules/categorie/${c.id}`)
             .then(r => [c.id, r.data.rules || []])
             .catch(() => [c.id, []])
        )
      );
      const map = {};
      results.forEach(([cid, arr]) => {
        map[cid] = (arr || []).map((r, i) => ({
          _k: `${cid}-${i}-${Date.now()}`,
          tipo: r.tipo || "incremento",
          valore: r.valore ?? "0",
          da_percent: r.da_percent ?? "",
          a_percent: r.a_percent ?? "",
          attivo: r.attivo !== false,
        }));
      });
      setRulesByCat(map);
      // 2) preview aggregato usando regole salvate (body vuoto → usa saved)
      const { data: preview } = await api.post(`/computi/${computo.id}/materiali-rules/preview`, { rules: [] });
      setBreakdown(preview);
    } catch (e) {
      console.warn("materiali-cat load:", e?.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) loadAll(); /* eslint-disable-line */ }, [open, computo.id]);

  const reloadPreview = async () => {
    try {
      const { data } = await api.post(`/computi/${computo.id}/materiali-rules/preview`, { rules: [] });
      setBreakdown(data);
    } catch (e) { console.warn("preview reload:", e?.message); }
  };

  const buildCatPayload = (cid) => ({
    rules: (rulesByCat[cid] || []).map(r => ({
      tipo: r.tipo,
      valore: Number(r.valore || 0),
      ...(r.da_percent !== "" && r.da_percent != null ? { da_percent: Number(r.da_percent) } : {}),
      ...(r.a_percent !== "" && r.a_percent != null ? { a_percent: Number(r.a_percent) } : {}),
      attivo: !!r.attivo,
    })),
  });

  const saveCat = async (cid) => {
    setSavingCat(s => ({ ...s, [cid]: true }));
    try {
      const payload = buildCatPayload(cid);
      if (payload.rules.length === 0) {
        await api.delete(`/computi/${computo.id}/materiali-rules/categorie/${cid}`);
      } else {
        await api.post(`/computi/${computo.id}/materiali-rules/categorie/${cid}`, payload);
      }
      toast.success("Regola categoria salvata (non ancora applicata).");
      await reloadPreview();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d ? JSON.stringify(d) : e.message));
    } finally { setSavingCat(s => ({ ...s, [cid]: false })); }
  };

  const addRule = (cid) => {
    setRulesByCat(prev => {
      const arr = prev[cid] || [];
      if (arr.length >= 10) { toast.error("Massimo 10 regole per categoria"); return prev; }
      return { ...prev, [cid]: [...arr, { _k: `${cid}-${Date.now()}`, tipo: "incremento", valore: "0", da_percent: "", a_percent: "", attivo: true }] };
    });
  };
  const updRule = (cid, idx, patch) => {
    setRulesByCat(prev => ({ ...prev, [cid]: (prev[cid] || []).map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  };
  const delRule = (cid, idx) => {
    setRulesByCat(prev => ({ ...prev, [cid]: (prev[cid] || []).filter((_, i) => i !== idx) }));
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/computi/${computo.id}/materiali-rules/apply`);
      toast.success(`Regole categoria applicate · righe: ${data.righe_processate || 0} · materiali: ${data.materiali_aggiornati || 0}`);
      setShowConfirm(false); setOpen(false);
      if (onApplied) onApplied();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d ? JSON.stringify(d) : e.message));
    } finally { setApplying(false); }
  };

  // per_categoria: costruisci mappa
  const perCatMap = {};
  (breakdown?.per_categoria || []).forEach(p => { perCatMap[p.categoria_id] = p; });

  const rows = (categorie || []).map(c => {
    const p = perCatMap[c.id];
    const has_rule = (rulesByCat[c.id] || []).length > 0;
    const importo = p ? Number(p.importo_lavorazioni || 0) : 0;
    const mat_orig = p ? Number(p.materiali_originali || 0) : 0;
    const mat_inv = p ? Number(p.materiali_inviluppo || 0) : 0;
    const has_analisi = !!p;
    const inc_orig = has_analisi && importo > 0 ? (mat_orig / importo * 100) : null;
    const inc_inv = has_analisi && importo > 0 ? (mat_inv / importo * 100) : null;
    const diff = has_analisi ? (mat_inv - mat_orig) : null;
    return { cat: c, has_analisi, has_rule, importo, mat_orig, mat_inv, inc_orig, inc_inv, diff, righe: p?.righe || 0 };
  });

  const totale = breakdown?.totale || null;
  const totInc_orig = totale && Number(totale.importo_lavorazioni) > 0
    ? Number(totale.materiali_originali) / Number(totale.importo_lavorazioni) * 100 : null;
  const totInc_inv = totale && Number(totale.importo_lavorazioni) > 0
    ? Number(totale.materiali_inviluppo) / Number(totale.importo_lavorazioni) * 100 : null;
  const totDiff = totale ? (Number(totale.materiali_inviluppo) - Number(totale.materiali_originali)) : null;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="inline-flex items-center gap-1 rounded-md border border-teal-500/40 bg-teal-500/10 px-2.5 py-1 text-xs font-medium text-teal-800 dark:text-teal-300 hover:bg-teal-500/20"
            data-testid="materiali-cat-trigger"
            title="Regole Materiali per Categoria Operativa"
          >
            <Sparkles className="h-3 w-3" /> Materiali per categorie
            {totale?.materiali_inviluppo && <span className="ml-1 font-mono">€ {fmt(totale.materiali_inviluppo)}</span>}
            <ChevronDown className="h-3 w-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[880px] max-w-[95vw] p-3 max-h-[80vh] overflow-y-auto" data-testid="materiali-cat-panel">
          <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
            <div>
              <div className="text-xs font-semibold">Regole MATERIALI per Categoria Operativa</div>
              <div className="text-[10px] text-slate-500 leading-tight">
                Priorità: <b>Categoria</b> → <b>Regole globali</b> → <b>Materiali Analisi (originale)</b>. Mai sommate.
              </div>
            </div>
            {totale && (
              <div className="text-[10px] text-slate-600 dark:text-slate-400 font-mono text-right">
                <div>Materiali Analisi · € {fmt(totale.materiali_originali)}{totInc_orig != null && <span> · {fmt(totInc_orig)}%</span>}</div>
                <div>Materiali INVILUPPO · <b>€ {fmt(totale.materiali_inviluppo)}</b>{totInc_inv != null && <span> · <b>{fmt(totInc_inv)}%</b></span>}</div>
                <div>Δ · € {fmt(totDiff)}</div>
              </div>
            )}
          </div>

          {/* Riepilogo per categoria — sezione 6 */}
          <div className="mb-3 rounded-md border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="materiali-cat-riepilogo">
            <div className="text-[10px] uppercase tracking-widest bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-600 dark:text-slate-400 font-semibold">Riepilogo Materiali per categoria</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 dark:bg-slate-900/40 text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1">Categoria</th>
                    <th className="text-right px-2 py-1">Importo lavorazioni</th>
                    <th className="text-right px-2 py-1">Materiali Analisi €</th>
                    <th className="text-right px-2 py-1">Inc. Analisi %</th>
                    <th className="text-right px-2 py-1">Materiali INVILUPPO €</th>
                    <th className="text-right px-2 py-1">Inc. INVILUPPO %</th>
                    <th className="text-right px-2 py-1">Δ €</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {rows.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-3 italic text-slate-500">Nessuna categoria.</td></tr>
                  )}
                  {rows.map(r => (
                    <tr key={r.cat.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`materiali-cat-row-${r.cat.id}`}>
                      <td className="px-2 py-1 font-sans">
                        {r.cat.nome}
                        {r.has_rule && <Badge variant="outline" className="ml-2 text-[9px] border-teal-500 text-teal-700 dark:text-teal-300" data-testid={`materiali-cat-hasrule-${r.cat.id}`}>Regola categoria</Badge>}
                      </td>
                      <td className="px-2 py-1 text-right">€ {fmt(r.importo)}</td>
                      <td className="px-2 py-1 text-right">{r.has_analisi ? `€ ${fmt(r.mat_orig)}` : <span className="text-slate-400 italic">— Nessun materiale da Analisi</span>}</td>
                      <td className="px-2 py-1 text-right">{r.has_analisi && r.inc_orig != null ? `${fmt(r.inc_orig)}%` : "—"}</td>
                      <td className="px-2 py-1 text-right">{r.has_analisi ? <b>€ {fmt(r.mat_inv)}</b> : "—"}</td>
                      <td className="px-2 py-1 text-right">{r.has_analisi && r.inc_inv != null ? <b>{fmt(r.inc_inv)}%</b> : "—"}</td>
                      <td className={`px-2 py-1 text-right ${r.diff > 0 ? "text-rose-600" : r.diff < 0 ? "text-emerald-600" : ""}`}>{r.has_analisi ? `€ ${fmt(r.diff)}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[10px] italic text-slate-500 px-2 py-1 border-t border-slate-100 dark:border-slate-800">
              Il confronto <b>Analisi vs INVILUPPO</b> considera SOLO i materiali generati da Analisi Prezzi. I materiali manuali sono esclusi.
            </div>
          </div>

          {loading && <div className="text-center text-xs text-slate-500 italic py-2">Caricamento regole per categoria…</div>}

          {/* Editor regole per categoria */}
          <Accordion type="multiple" value={openAcc} onValueChange={setOpenAcc} className="space-y-1.5">
            {(categorie || []).map(c => {
              const rules = rulesByCat[c.id] || [];
              const hasRule = rules.length > 0;
              const r = rows.find(x => x.cat.id === c.id);
              return (
                <AccordionItem key={c.id} value={c.id} className="border border-slate-200 dark:border-slate-800 rounded-md bg-slate-50/40 dark:bg-slate-900/40" data-testid={`materiali-cat-item-${c.id}`}>
                  <AccordionTrigger className="px-3 py-2 hover:no-underline">
                    <div className="flex-1 flex items-center justify-between pr-2 gap-2">
                      <div className="text-left flex items-center gap-2 flex-wrap">
                        <Tags className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                        <span className="text-sm font-semibold">{c.nome}</span>
                        {hasRule && <Badge variant="outline" className="text-[10px] border-teal-500 text-teal-700 dark:text-teal-300">Regola categoria</Badge>}
                        {r?.has_analisi && <span className="text-[10px] text-slate-500">{r.righe} righe · € {fmt(r.mat_inv)}</span>}
                        {r && !r.has_analisi && <span className="text-[10px] text-slate-400 italic">Nessun materiale da Analisi</span>}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    {rules.length === 0 && (
                      <div className="text-[11px] text-slate-500 italic py-1">Nessuna regola specifica. Eredita eventuali regole globali o resta = Materiali Analisi.</div>
                    )}
                    {rules.map((rr, idx) => (
                      <div key={rr._k} className="border border-slate-200 dark:border-slate-800 rounded-md p-2 mb-2 bg-white dark:bg-slate-900/60">
                        <div className="grid grid-cols-[1fr_100px_28px] gap-2 items-center">
                          <select value={rr.tipo}
                                  onChange={(e) => updRule(c.id, idx, { tipo: e.target.value })}
                                  disabled={!canEdit}
                                  className="h-8 text-xs rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2"
                                  data-testid={`materiali-cat-tipo-${c.id}-${idx}`}>
                            <option value="incremento">Incremento %</option>
                            <option value="fisso">Fisso %</option>
                            <option value="punti">+ Punti %</option>
                          </select>
                          <Input type="number" step="0.01" placeholder="Valore" value={rr.valore}
                                 onChange={(e) => updRule(c.id, idx, { valore: e.target.value })}
                                 disabled={!canEdit} className="h-8 text-xs"
                                 data-testid={`materiali-cat-val-${c.id}-${idx}`} />
                          <Button size="icon" variant="ghost" onClick={() => delRule(c.id, idx)} disabled={!canEdit} className="h-7 w-7" data-testid={`materiali-cat-del-${c.id}-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center mt-2 text-[11px]">
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500 shrink-0">Da % inc. orig.</span>
                            <Input type="number" step="0.01" placeholder="—" value={rr.da_percent}
                                   onChange={(e) => updRule(c.id, idx, { da_percent: e.target.value })}
                                   disabled={!canEdit} className="h-7 text-xs"
                                   data-testid={`materiali-cat-da-${c.id}-${idx}`} />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500 shrink-0">a %</span>
                            <Input type="number" step="0.01" placeholder="—" value={rr.a_percent}
                                   onChange={(e) => updRule(c.id, idx, { a_percent: e.target.value })}
                                   disabled={!canEdit} className="h-7 text-xs"
                                   data-testid={`materiali-cat-a-${c.id}-${idx}`} />
                          </div>
                          <label className="text-[10px] flex items-center gap-1 whitespace-nowrap">
                            <input type="checkbox" checked={rr.attivo}
                                   onChange={(e) => updRule(c.id, idx, { attivo: e.target.checked })}
                                   disabled={!canEdit}
                                   data-testid={`materiali-cat-attivo-${c.id}-${idx}`} /> attivo
                          </label>
                        </div>
                      </div>
                    ))}
                    <div className="flex flex-wrap gap-2 justify-between items-center pt-1">
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => addRule(c.id)} disabled={rules.length >= 10} className="h-7 text-xs" data-testid={`materiali-cat-add-${c.id}`}>
                          <Plus className="h-3 w-3 mr-1" /> Regola
                        </Button>
                      )}
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => saveCat(c.id)} disabled={!!savingCat[c.id]} className="h-7 text-xs" data-testid={`materiali-cat-save-${c.id}`}>
                          {savingCat[c.id] ? "Salvataggio…" : "Salva regola categoria"}
                        </Button>
                      )}
                    </div>
                    <div className="text-[10px] italic text-slate-500 mt-1">
                      Salvare la regola non modifica i materiali. Applica le regole con il pulsante <b>Applica</b> in basso.
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-slate-200 dark:border-slate-700">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="materiali-cat-cancel">Chiudi</Button>
            <Button size="sm" onClick={() => setShowConfirm(true)} disabled={!canEdit || applying}
                    className="bg-teal-500 hover:bg-teal-600 text-slate-950 font-semibold"
                    data-testid="materiali-cat-apply">
              {applying ? "Applicazione…" : "Applica regole Materiali"}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md" data-testid="materiali-cat-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Applicare le regole MATERIALI (categoria + globali)?</DialogTitle>
            <DialogDescription>
              Verranno ricalcolati i prezzi INVILUPPO dei soli materiali generati da Analisi Prezzi seguendo la priorità:
              <br />
              <b>Categoria → Globali → Analisi originale</b> (mai sommate).
              <br /><br />
              Materiali <b>manuali</b>, quantità e prezzi delle lavorazioni restano invariati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConfirm(false)} data-testid="materiali-cat-confirm-cancel">Annulla</Button>
            <Button onClick={runApply} disabled={applying} data-testid="materiali-cat-confirm-ok"
                    className="bg-teal-500 hover:bg-teal-600 text-slate-950 font-semibold">
              {applying ? "Applicazione…" : "Conferma e applica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ============================================================================
// FASE C.2 — Compila Auto MAT + MDO (INVILUPPO STANDARD)
// Trigger → Modal preview (§22-24) → Apply (§25) / Reset / Ricalcola (§28)
// FASE C.2.1 — supporta autoOpen (hook post-import XPWE): apre la Preview
// automaticamente solo se lo snapshot NON è già applicato.
// ============================================================================
function InviluppoAutoBadge({ computo, canEdit, onApplied, autoOpen = false, onAutoOpened }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const autoTriggeredRef = useRef(false);
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

  const loadStatus = async () => {
    try { const { data } = await api.get(`/computi/${computo.id}/inviluppo-auto/status`); setStatus(data); return data; }
    catch (e) { return null; }
  };
  useEffect(() => { loadStatus(); /* eslint-disable-line */ }, [computo.id]);

  // FASE C.4 — override MDO/MAT per categoria (state locale, resettato ad ogni apertura)
  const [overrides, setOverrides] = useState({});
  const debounceRef = useRef(null);

  const openPreview = async () => {
    setOpen(true); setLoading(true); setPreview(null);
    setOverrides({});  // reset ad ogni apertura
    try {
      // GET carica preset + eventuali override già salvati sul computo
      const { data } = await api.get(`/computi/${computo.id}/inviluppo-auto/preview`);
      setPreview(data);
      // Inizializza il draft con gli override PERSISTITI (se presenti)
      const persisted = data.overrides_active || {};
      setOverrides(persisted);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  // FASE C.4 — quando cambio un override, ricalcolo la preview via POST (debounce 250ms)
  const refreshPreviewWithOverrides = async (nextOverrides) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/computi/${computo.id}/inviluppo-auto/preview`, { overrides: nextOverrides });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const setOverrideValue = (codice, field, rawValue) => {
    // rawValue può essere stringa vuota → rimuove quell'override
    setOverrides(prev => {
      const next = { ...prev };
      const cur = { ...(next[codice] || {}) };
      if (rawValue === "" || rawValue == null) {
        delete cur[field];
      } else {
        // accetta numeri con - e virgola
        cur[field] = String(rawValue).replace(",", ".");
      }
      if (Object.keys(cur).length === 0) {
        delete next[codice];
      } else {
        next[codice] = cur;
      }
      // debounced preview refresh
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => refreshPreviewWithOverrides(next), 250);
      return next;
    });
  };

  const resetCategoryOverride = (codice) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[codice];
      if (debounceRef.current) clearTimeout(debounceRef.current);
      refreshPreviewWithOverrides(next);
      return next;
    });
  };

  // FASE C.5.2 — Rimuove SOLO uno tra mdo_cost e mat_cost (persistito in DB
  // ma non più applicabile). Preserva gli altri override della categoria.
  const removeCategoryCostField = (codice, field) => {
    const rule = (preview?.category_rules || []).find(r => r.codice === codice) || {};
    setOverrides(prev => {
      const cur = { ...(prev[codice] || {}) };
      // Sincronizza da DB (rule) → local state, tranne il field da rimuovere
      const dbMdoVar = rule.effective_mdo_var != null && rule.is_override ? rule.effective_mdo_var : null;
      const dbMatVar = rule.effective_mat_var != null && rule.is_override ? rule.effective_mat_var : null;
      if (cur.mdo_var === undefined && dbMdoVar !== null && dbMdoVar !== rule.preset_mdo_var) cur.mdo_var = dbMdoVar;
      if (cur.mat_var === undefined && dbMatVar !== null && dbMatVar !== rule.preset_mat_var) cur.mat_var = dbMatVar;
      if (cur.mdo_cost === undefined && rule.mdo_cost != null && field !== "mdo_cost") cur.mdo_cost = rule.mdo_cost;
      if (cur.mat_cost === undefined && rule.mat_cost != null && field !== "mat_cost") cur.mat_cost = rule.mat_cost;
      delete cur[field];
      const next = { ...prev };
      if (Object.keys(cur).length === 0) {
        delete next[codice];
      } else {
        next[codice] = cur;
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      refreshPreviewWithOverrides(next);
      return next;
    });
  };

  // FASE C.2.1 — Hook post-import XPWE: apre la Preview automaticamente
  // ma SOLO se lo snapshot Auto INVILUPPO non è già applicato.
  useEffect(() => {
    if (!autoOpen || autoTriggeredRef.current) return;
    autoTriggeredRef.current = true;
    (async () => {
      const st = await loadStatus();
      if (st && st.applied === true) {
        // Già applicato → non riproporre. Consuma comunque il flag.
        if (onAutoOpened) onAutoOpened();
        return;
      }
      await openPreview();
      if (onAutoOpened) onAutoOpened();
    })();
    // eslint-disable-next-line
  }, [autoOpen, computo?.id]);

  const doApply = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/computi/${computo.id}/inviluppo-auto/apply`, { overrides });
      toast.success(
        `Auto INVILUPPO applicato · Righe: ${data.righe_applicate} · ` +
        `Manuali protette: ${data.righe_saltate_manual} · ` +
        `Non economiche: ${data.righe_saltate_non_economic} · ` +
        `Margine: € ${fmt(data.totale.margine_residuo)} (${data.totale.margine_residuo_percent}%)`
      );
      setConfirmApply(false); setOpen(false);
      await loadStatus();
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const doReset = async () => {
    setLoading(true);
    try {
      const { data } = await api.delete(`/computi/${computo.id}/inviluppo-auto`);
      toast.success(`Snapshot Auto rimosso · righe pulite: ${data.righe_pulite}`);
      setConfirmReset(false);
      await loadStatus();
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const statusColor = (s) => s === "in_target" ? "emerald" : s === "above_target" ? "sky" : "amber";
  const statusLabel = (s) => s === "in_target" ? "IN OBIETTIVO" : s === "above_target" ? "SOPRA OBIETTIVO" : s === "below_target" ? "SOTTO OBIETTIVO" : "N/A";
  const modeLabel = (m) => ({
    specific_rule: "Regola categoria", missing_mdo_fallback: "Fallback MDO mancante",
    out_of_range_fallback: "Fallback fuori intervallo", demolition_30: "Fallback demolizioni 30%",
    category_fallback_30: "Fallback categoria 30%", needs_review: "Da verificare",
    non_economic_row: "Riga non economica",
  }[m] || m);

  return (
    <>
      <button
        onClick={openPreview}
        className="inline-flex items-center gap-1 rounded-md border border-violet-500/50 bg-violet-500/10 px-2.5 py-1 text-xs font-medium text-violet-800 dark:text-violet-300 hover:bg-violet-500/20"
        data-testid="inviluppo-auto-trigger"
        title="Compila Auto INVILUPPO — MDO + Materiali + Noli"
      >
        <Sparkles className="h-3 w-3" /> Compila Auto MAT + MDO
        {status?.applied && (
          <Badge variant="outline" className={`ml-1 text-[9px] border-${statusColor(status.totale?.margin_status)}-500`}>
            {statusLabel(status.totale?.margin_status)} · {status.totale?.margine_residuo_percent}%
          </Badge>
        )}
        {status?.is_stale && <Badge variant="outline" className="ml-1 text-[9px] border-amber-500 text-amber-700">stale</Badge>}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[1100px] max-h-[85vh] overflow-y-auto" data-testid="inviluppo-auto-modal">
          <DialogHeader>
            <DialogTitle>Compilazione Auto MDO + Materiali</DialogTitle>
            <DialogDescription>
              Preset: <b>INVILUPPO STANDARD v{preview?.preset_version || 1}</b> · Target margine: <b>25%–35%</b>
            </DialogDescription>
          </DialogHeader>

          {loading && <div className="text-sm text-slate-500">Caricamento…</div>}

          {preview && !status?.applied && (
            <div className="rounded-md border border-violet-300 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/30 p-2 text-[11px] text-violet-800 dark:text-violet-200" data-testid="inv-preview-not-saved-banner">
              <b>ANTEPRIMA — NON ANCORA SALVATA.</b> Controlla i valori e premi
              <b> APPLICA COMPILAZIONE AUTO </b>per salvarli nel computo.
            </div>
          )}

          {preview && (
            <>
              {/* Cards riepilogo generale */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <div className="border rounded-md p-2 bg-slate-50/70 dark:bg-slate-900/40">
                  <div className="uppercase tracking-widest text-slate-500 text-[9px]">Importo computo</div>
                  <div className="font-mono font-semibold" data-testid="inv-importo">€ {fmt(preview.totale.importo_computo)}</div>
                  <div className="text-[9px] text-slate-500">{preview.totale.voci_economiche} voci · {preview.totale.non_economic_rows} non economiche</div>
                </div>
                <div className="border rounded-md p-2 bg-sky-50/50 dark:bg-sky-950/20">
                  <div className="uppercase tracking-widest text-sky-600 text-[9px]">MDO prevista</div>
                  <div className="font-mono font-semibold">€ {fmt(preview.totale.mdo_prevista)}</div>
                  <div className="uppercase tracking-widest text-emerald-600 text-[9px] mt-1">Materiali previsti</div>
                  <div className="font-mono font-semibold">€ {fmt(preview.totale.materiali_previsti)}</div>
                  <div className="uppercase tracking-widest text-orange-600 text-[9px] mt-1">Noli/Attrezzature</div>
                  <div className="font-mono font-semibold">€ {fmt(preview.totale.noli_previsti)}</div>
                </div>
                <div className="border rounded-md p-2 bg-slate-50/70 dark:bg-slate-900/40">
                  <div className="uppercase tracking-widest text-slate-500 text-[9px]">Costo previsto</div>
                  <div className="font-mono font-semibold text-lg" data-testid="inv-costo">€ {fmt(preview.totale.costo_previsto)}</div>
                </div>
                <div className={`border rounded-md p-2 bg-${statusColor(preview.totale.margin_status)}-50/50 dark:bg-${statusColor(preview.totale.margin_status)}-950/20 border-${statusColor(preview.totale.margin_status)}-500/40`}>
                  <div className={`uppercase tracking-widest text-${statusColor(preview.totale.margin_status)}-600 text-[9px]`}>Margine residuo</div>
                  <div className="font-mono font-semibold text-lg" data-testid="inv-margine">€ {fmt(preview.totale.margine_residuo)}</div>
                  <div className="text-[10px] font-mono">{preview.totale.margine_residuo_percent}% · <b>{statusLabel(preview.totale.margin_status)}</b></div>
                </div>
              </div>

              <div className="text-[10px] text-slate-500 my-2">
                Distribuzione: specific_rule={preview.modes_totali.specific_rule} · missing_mdo={preview.modes_totali.missing_mdo_fallback} · out_of_range={preview.modes_totali.out_of_range_fallback} · demolition_30={preview.modes_totali.demolition_30} · category_fallback={preview.modes_totali.category_fallback_30} · needs_review={preview.modes_totali.needs_review} · non_economic={preview.modes_totali.non_economic_row}
              </div>

              {/* Tabella per categoria */}
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto border rounded-md">
                <table className="w-full text-[11px]" data-testid="inv-cat-table">
                  <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                    <tr className="text-left">
                      <th className="px-2 py-1">Categoria</th>
                      <th className="px-2 py-1 text-right">Voci</th>
                      <th className="px-2 py-1 text-right">Importo</th>
                      <th className="px-2 py-1 text-right" title="Variazione MDO categoria">MDO %</th>
                      <th className="px-2 py-1 text-right" title="Variazione Materiali categoria">MAT %</th>
                      <th className="px-2 py-1 text-right" title="Costo totale MDO manuale (distribuito ponderato)">Costo MDO €</th>
                      <th className="px-2 py-1 text-right" title="Costo totale Materiali manuale (distribuito ponderato)">Costo MAT €</th>
                      <th className="px-2 py-1 text-right">MDO</th>
                      <th className="px-2 py-1 text-right">Mat</th>
                      <th className="px-2 py-1 text-right">Noli</th>
                      <th className="px-2 py-1 text-right">Costo</th>
                      <th className="px-2 py-1 text-right">Margine</th>
                      <th className="px-2 py-1 text-right">%</th>
                      <th className="px-2 py-1">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.per_categoria.map(c => {
                      // Trova regola dal category_rules per esporre editing
                      const rule = (preview.category_rules || []).find(r => r.codice === c.codice);
                      // FASE C.4.1 — applicabilità override % per riga:
                      //   editable = specific (kind) AND permesso utente AND almeno 1 riga specific_rule
                      const hasSpecificKind = rule?.is_editable;
                      const overrideApplicable = rule?.override_applicable ?? hasSpecificKind;
                      const editable = hasSpecificKind && canEdit && overrideApplicable;
                      // FASE C.5.2 — applicabilità distribuzione costi MDO/MAT (indipendenti)
                      const mdoCostApplicable = rule?.mdo_cost_applicable ?? hasSpecificKind;
                      const matCostApplicable = rule?.mat_cost_applicable ?? hasSpecificKind;
                      const mdoCostEditable = hasSpecificKind && canEdit && mdoCostApplicable;
                      const matCostEditable = hasSpecificKind && canEdit && matCostApplicable;
                      const mdoCostElig = rule?.mdo_cost_eligible_rows ?? 0;
                      const matCostElig = rule?.mat_cost_eligible_rows ?? 0;
                      const ov = overrides[c.codice] || {};
                      const mdoDisplay = ov.mdo_var !== undefined ? ov.mdo_var : (rule?.effective_mdo_var ?? "");
                      const matDisplay = ov.mat_var !== undefined ? ov.mat_var : (rule?.effective_mat_var ?? "");
                      const mdoCostDisplay = ov.mdo_cost !== undefined ? ov.mdo_cost : (rule?.mdo_cost ?? "");
                      const matCostDisplay = ov.mat_cost !== undefined ? ov.mat_cost : (rule?.mat_cost ?? "");
                      const isOv = rule?.is_editable && (
                        ov.mdo_var !== undefined || ov.mat_var !== undefined ||
                        ov.mdo_cost !== undefined || ov.mat_cost !== undefined ||
                        rule.mdo_cost != null || rule.mat_cost != null
                      );
                      // C.4.1 — coverage: righe eleggibili all'override %
                      const total = rule?.total_rows ?? 0;
                      const eligible = rule?.eligible_rows ?? 0;
                      const excluded = total - eligible;
                      const reasons = rule?.ineligible_reasons || {};
                      const reasonBits = [];
                      if (reasons.missing_mdo) reasonBits.push(`${reasons.missing_mdo} senza IncMDO`);
                      if (reasons.out_of_range) reasonBits.push(`${reasons.out_of_range} fuori range`);
                      if (reasons.demolition) reasonBits.push(`${reasons.demolition} demolizione`);
                      if (reasons.category_fallback) reasonBits.push(`${reasons.category_fallback} senza regola`);
                      if (reasons.needs_review) reasonBits.push(`${reasons.needs_review} needs_review`);
                      if (reasons.non_economic) reasonBits.push(`${reasons.non_economic} non economica`);
                      const reasonsText = reasonBits.join(" · ");
                      const disabledTooltip = hasSpecificKind && !overrideApplicable
                        ? `Override % non applicabile: 0/${total} voci con regola specifica (${reasonsText || "tutte in fallback"})`
                        : (excluded > 0 ? `Override applicato a ${eligible}/${total} voci · escluse: ${reasonsText}` : undefined);
                      const mdoCostTooltip = hasSpecificKind && !mdoCostApplicable
                        ? `Costo MDO € non distribuibile: nessuna voce con peso teorico MDO valido (${total} righe totali)`
                        : (mdoCostElig < total ? `Costo MDO € distribuito su ${mdoCostElig}/${total} voci con peso MDO valido` : "Vuoto = usa %. Impostando un valore, MDO categoria = questo importo (distribuito ponderato).");
                      const matCostTooltip = hasSpecificKind && !matCostApplicable
                        ? `Costo Materiali € non distribuibile: nessuna voce con peso teorico Materiali valido (${total} righe totali)`
                        : (matCostElig < total ? `Costo Materiali € distribuito su ${matCostElig}/${total} voci con peso Materiali valido` : "Vuoto = usa %. Impostando un valore, Materiali categoria = questo importo (distribuito ponderato).");
                      // Persist stale? override/costs already saved on DB but no longer applicable
                      const savedMdoCost = rule?.mdo_cost;
                      const savedMatCost = rule?.mat_cost;
                      const mdoCostStale = savedMdoCost != null && !mdoCostApplicable;
                      const matCostStale = savedMatCost != null && !matCostApplicable;
                      // FASE C.6 — Layout: pure_fallback / mixed / pure_specific / none
                      const layout = rule?.layout || (hasSpecificKind ? "pure_specific" : "none");
                      const fbRows = rule?.fallback_rows ?? 0;
                      const fbImporto = Number(rule?.fallback_importo ?? 0);
                      const fbFmpDb = rule?.fallback_margin_pct;
                      const fbFmdoDb = rule?.fallback_mdo_cost;
                      const fbFmatDb = rule?.fallback_mat_cost;
                      const fbFmpDisplay = ov.fallback_margin_pct !== undefined ? ov.fallback_margin_pct : (fbFmpDb ?? "");
                      const fbFmdoDisplay = ov.fallback_mdo_cost !== undefined ? ov.fallback_mdo_cost : (fbFmdoDb ?? "");
                      const fbFmatDisplay = ov.fallback_mat_cost !== undefined ? ov.fallback_mat_cost : (fbFmatDb ?? "");
                      const fmpEffective = fbFmpDisplay !== "" ? Number(fbFmpDisplay) : 30;
                      const fbCostoTarget = fbImporto * (100 - fmpEffective) / 100;
                      const fbAssignedMdo = fbFmdoDisplay !== "" ? Number(fbFmdoDisplay) : 0;
                      const fbAssignedMat = fbFmatDisplay !== "" ? Number(fbFmatDisplay) : 0;
                      const fbUnspecified = fbCostoTarget - fbAssignedMdo - fbAssignedMat;
                      const fbOvershoot = (fbAssignedMdo + fbAssignedMat) > fbCostoTarget + 0.005;
                      const isFbOv = fbFmpDb != null || fbFmdoDb != null || fbFmatDb != null || ov.fallback_margin_pct !== undefined || ov.fallback_mdo_cost !== undefined || ov.fallback_mat_cost !== undefined;
                      const fbEditable = fbRows > 0 && canEdit;
                      return (
                        <Fragment key={c.categoria_id || c.codice || "none"}>
                        <tr className={`border-t border-slate-200 dark:border-slate-700 ${(isOv || isFbOv) ? "bg-amber-50/60 dark:bg-amber-950/20" : ""} ${hasSpecificKind && !overrideApplicable && layout !== "pure_fallback" ? "opacity-70" : ""}`}
                            data-testid={`inv-cat-row-${c.codice || "none"}`}>
                          <td className="px-2 py-1">
                            <div>{c.nome}</div>
                            {hasSpecificKind && total > 0 && excluded > 0 && layout !== "pure_fallback" && (
                              <div className={`text-[9px] mt-0.5 ${overrideApplicable ? "text-amber-700 dark:text-amber-400" : "text-rose-700 dark:text-rose-400"}`}
                                   data-testid={`inv-cat-coverage-${c.codice}`}
                                   title={reasonsText}>
                                {overrideApplicable
                                  ? `⚠ Override % applicabile a ${eligible}/${total} voci`
                                  : `⛔ 0/${total} voci con regola specifica`}
                                {reasonsText && (<span className="ml-1 italic">({reasonsText})</span>)}
                              </div>
                            )}
                            {layout === "pure_fallback" && (
                              <div className="text-[9px] mt-0.5 text-slate-500 italic" data-testid={`inv-cat-pure-fallback-${c.codice}`}>
                                Categoria fallback: {fbRows} voci · Margine editabile
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right">{c.voci}</td>
                          <td className="px-2 py-1 text-right font-mono">{fmt(c.importo_computo)}</td>
                          {layout === "pure_fallback" ? (
                            <>
                              {/* MDO% column repurposed as Margine fallback % */}
                              <td className="px-2 py-1 text-right">
                                <input
                                  type="number"
                                  step="0.01"
                                  disabled={!fbEditable}
                                  placeholder="30"
                                  className={`w-16 text-right font-mono border rounded px-1 border-amber-400 ${fbEditable ? "bg-amber-50 dark:bg-amber-950/40" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                  value={fbFmpDisplay}
                                  onChange={(e) => setOverrideValue(c.codice, "fallback_margin_pct", e.target.value)}
                                  data-testid={`inv-cat-fb-margin-${c.codice}`}
                                  title="Margine % fallback (default 30). Modifica il costo target categoria."
                                />
                                <div className="text-[8px] text-amber-700 dark:text-amber-400 italic">Margine %</div>
                              </td>
                              {/* MAT% column shows dash for pure_fallback */}
                              <td className="px-2 py-1 text-right"><span className="text-slate-400 text-[10px]">—</span></td>
                              {/* Costo MDO € (fallback_mdo_cost) */}
                              <td className="px-2 py-1 text-right">
                                <input
                                  type="number" step="0.01" min="0" placeholder="—"
                                  disabled={!fbEditable}
                                  className={`w-20 text-right font-mono border rounded px-1 border-amber-400 ${fbEditable ? "bg-amber-50 dark:bg-amber-950/40" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                  value={fbFmdoDisplay}
                                  onChange={(e) => setOverrideValue(c.codice, "fallback_mdo_cost", e.target.value)}
                                  data-testid={`inv-cat-fb-mdo-cost-${c.codice}`}
                                  title="Costo MDO € manuale (allocato proporzionalmente all'importo di riga)."
                                />
                              </td>
                              {/* Costo MAT € (fallback_mat_cost) */}
                              <td className="px-2 py-1 text-right">
                                <input
                                  type="number" step="0.01" min="0" placeholder="—"
                                  disabled={!fbEditable}
                                  className={`w-20 text-right font-mono border rounded px-1 border-amber-400 ${fbEditable ? "bg-amber-50 dark:bg-amber-950/40" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                  value={fbFmatDisplay}
                                  onChange={(e) => setOverrideValue(c.codice, "fallback_mat_cost", e.target.value)}
                                  data-testid={`inv-cat-fb-mat-cost-${c.codice}`}
                                  title="Costo Materiali € manuale."
                                />
                              </td>
                            </>
                          ) : (
                            <>
                          <td className="px-2 py-1 text-right">
                            {hasSpecificKind ? (
                              <input
                                type="number"
                                step="0.01"
                                disabled={!editable}
                                className={`w-16 text-right font-mono border rounded px-1 ${editable ? "bg-white dark:bg-slate-900" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                value={mdoDisplay}
                                onChange={(e) => setOverrideValue(c.codice, "mdo_var", e.target.value)}
                                data-testid={`inv-cat-mdo-var-${c.codice}`}
                                title={disabledTooltip}
                              />
                            ) : (
                              <span className="text-slate-400 text-[10px]" data-testid={`inv-cat-mdo-var-${c.codice || "none"}-ro`}>—</span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {hasSpecificKind ? (
                              <input
                                type="number"
                                step="0.01"
                                disabled={!editable}
                                className={`w-16 text-right font-mono border rounded px-1 ${editable ? "bg-white dark:bg-slate-900" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                value={matDisplay}
                                onChange={(e) => setOverrideValue(c.codice, "mat_var", e.target.value)}
                                data-testid={`inv-cat-mat-var-${c.codice}`}
                                title={disabledTooltip}
                              />
                            ) : (
                              <span className="text-slate-400 text-[10px]">—</span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {hasSpecificKind ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="—"
                                disabled={!mdoCostEditable}
                                className={`w-20 text-right font-mono border rounded px-1 ${mdoCostEditable ? "bg-white dark:bg-slate-900" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                value={mdoCostDisplay}
                                onChange={(e) => setOverrideValue(c.codice, "mdo_cost", e.target.value)}
                                data-testid={`inv-cat-mdo-cost-${c.codice}`}
                                title={mdoCostTooltip}
                              />
                            ) : (
                              <span className="text-slate-400 text-[10px]">—</span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {hasSpecificKind ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="—"
                                disabled={!matCostEditable}
                                className={`w-20 text-right font-mono border rounded px-1 ${matCostEditable ? "bg-white dark:bg-slate-900" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                value={matCostDisplay}
                                onChange={(e) => setOverrideValue(c.codice, "mat_cost", e.target.value)}
                                data-testid={`inv-cat-mat-cost-${c.codice}`}
                                title={matCostTooltip}
                              />
                            ) : (
                              <span className="text-slate-400 text-[10px]">—</span>
                            )}
                          </td>
                            </>
                          )}
                          <td className="px-2 py-1 text-right font-mono text-sky-700 dark:text-sky-300">{fmt(c.mdo_prevista)}</td>
                          <td className="px-2 py-1 text-right font-mono text-emerald-700 dark:text-emerald-300">{fmt(c.materiali_previsti)}</td>
                          <td className="px-2 py-1 text-right font-mono text-orange-700 dark:text-orange-300">{fmt(c.noli_previsti)}</td>
                          <td className="px-2 py-1 text-right font-mono">{fmt(c.costo_previsto)}</td>
                          <td className="px-2 py-1 text-right font-mono">{fmt(c.margine_residuo)}</td>
                          <td className="px-2 py-1 text-right font-mono">{c.margine_residuo_percent}%</td>
                          <td className="px-2 py-1 text-[10px]">
                            {layout === "pure_fallback" ? (
                              isFbOv ? (
                                <div className="flex flex-col gap-0.5" data-testid={`inv-cat-fb-badge-${c.codice}`}>
                                  <span className="text-amber-700 dark:text-amber-300 font-semibold">Fallback personalizzato</span>
                                  {fbOvershoot && (
                                    <span className="text-[9px] text-rose-700 dark:text-rose-400 font-semibold" data-testid={`inv-cat-fb-needsreview-${c.codice}`}>⚠ NEEDS REVIEW: MDO+MAT &gt; costo target</span>
                                  )}
                                  <button type="button" className="text-[10px] text-sky-700 dark:text-sky-300 underline text-left" onClick={() => resetCategoryOverride(c.codice)} data-testid={`inv-cat-reset-${c.codice}`}>Ripristina 30%</button>
                                </div>
                              ) : (
                                <span className="text-slate-500" data-testid={`inv-cat-fb-badge-${c.codice}`}>Fallback 30%</span>
                              )
                            ) : rule?.is_editable ? (
                              !overrideApplicable ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-rose-700 dark:text-rose-400 font-semibold" data-testid={`inv-cat-badge-${c.codice}`} title={disabledTooltip}>Non applicabile</span>
                                  {isOv && (
                                    <>
                                      <span className="text-[9px] text-slate-500 italic">Override salvato ma non attivo</span>
                                      <button type="button" className="text-[10px] text-sky-700 dark:text-sky-300 underline text-left" onClick={() => resetCategoryOverride(c.codice)} data-testid={`inv-cat-reset-${c.codice}`}>Rimuovi override</button>
                                    </>
                                  )}
                                </div>
                              ) : isOv ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-amber-700 dark:text-amber-300 font-semibold" data-testid={`inv-cat-badge-${c.codice}`}>
                                    Personalizzato{excluded > 0 ? ` (${eligible}/${total})` : ""}
                                  </span>
                                  {(ov.mdo_cost !== undefined || rule.mdo_cost != null) && !mdoCostStale && (
                                    <span className="text-[9px] text-sky-700 dark:text-sky-300">MDO: costo €</span>
                                  )}
                                  {(ov.mat_cost !== undefined || rule.mat_cost != null) && !matCostStale && (
                                    <span className="text-[9px] text-emerald-700 dark:text-emerald-300">MAT: costo €</span>
                                  )}
                                  {mdoCostStale && (
                                    <span className="text-[9px] text-rose-700 dark:text-rose-300 italic flex flex-col" data-testid={`inv-cat-mdo-cost-stale-${c.codice}`}>
                                      MDO: costo salvato non applicabile
                                      <button type="button" className="underline text-left text-sky-700 dark:text-sky-300" onClick={() => removeCategoryCostField(c.codice, "mdo_cost")} data-testid={`inv-cat-remove-mdo-cost-${c.codice}`}>Rimuovi costo MDO</button>
                                    </span>
                                  )}
                                  {matCostStale && (
                                    <span className="text-[9px] text-rose-700 dark:text-rose-300 italic flex flex-col" data-testid={`inv-cat-mat-cost-stale-${c.codice}`}>
                                      MAT: costo salvato non applicabile
                                      <button type="button" className="underline text-left text-sky-700 dark:text-sky-300" onClick={() => removeCategoryCostField(c.codice, "mat_cost")} data-testid={`inv-cat-remove-mat-cost-${c.codice}`}>Rimuovi costo MAT</button>
                                    </span>
                                  )}
                                  <button type="button" className="text-[10px] text-sky-700 dark:text-sky-300 underline text-left" onClick={() => resetCategoryOverride(c.codice)} data-testid={`inv-cat-reset-${c.codice}`}>Ripristina tutto</button>
                                </div>
                              ) : (
                                <span className="text-slate-500" data-testid={`inv-cat-badge-${c.codice}`}>Preset v{preview.preset_version}</span>
                              )
                            ) : (
                              c.in_preset ? "✓ specifico" : "fallback 30%"
                            )}
                          </td>
                        </tr>
                        {layout === "mixed" && (
                          <tr className={`${isFbOv ? "bg-amber-50/40 dark:bg-amber-950/10" : "bg-slate-50/50 dark:bg-slate-900/30"}`} data-testid={`inv-cat-fb-row-${c.codice}`}>
                            <td className="px-2 py-1 pl-8 text-[10px] text-amber-700 dark:text-amber-400 italic">
                              ↳ Fallback ({fbRows} voci · €{fmt(fbImporto)})
                            </td>
                            <td className="px-2 py-1 text-right text-[10px] text-slate-500">{fbRows}</td>
                            <td className="px-2 py-1 text-right text-[10px] font-mono">{fmt(fbImporto)}</td>
                            <td className="px-2 py-1 text-right">
                              <input
                                type="number" step="0.01" placeholder="30" disabled={!fbEditable}
                                className={`w-16 text-right font-mono border rounded px-1 border-amber-400 ${fbEditable ? "bg-amber-50 dark:bg-amber-950/40" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                value={fbFmpDisplay}
                                onChange={(e) => setOverrideValue(c.codice, "fallback_margin_pct", e.target.value)}
                                data-testid={`inv-cat-fb-margin-${c.codice}`}
                                title="Margine % fallback"
                              />
                              <div className="text-[8px] text-amber-700 italic">Margine%</div>
                            </td>
                            <td><span className="text-slate-400 text-[10px]">—</span></td>
                            <td className="px-2 py-1 text-right">
                              <input
                                type="number" step="0.01" min="0" placeholder="—" disabled={!fbEditable}
                                className={`w-20 text-right font-mono border rounded px-1 border-amber-400 ${fbEditable ? "bg-amber-50 dark:bg-amber-950/40" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                value={fbFmdoDisplay}
                                onChange={(e) => setOverrideValue(c.codice, "fallback_mdo_cost", e.target.value)}
                                data-testid={`inv-cat-fb-mdo-cost-${c.codice}`}
                                title="Costo MDO € manuale sulle righe fallback"
                              />
                            </td>
                            <td className="px-2 py-1 text-right">
                              <input
                                type="number" step="0.01" min="0" placeholder="—" disabled={!fbEditable}
                                className={`w-20 text-right font-mono border rounded px-1 border-amber-400 ${fbEditable ? "bg-amber-50 dark:bg-amber-950/40" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed"}`}
                                value={fbFmatDisplay}
                                onChange={(e) => setOverrideValue(c.codice, "fallback_mat_cost", e.target.value)}
                                data-testid={`inv-cat-fb-mat-cost-${c.codice}`}
                                title="Costo Materiali € manuale sulle righe fallback"
                              />
                            </td>
                            <td className="px-2 py-1 text-right text-[10px] font-mono text-slate-500" colSpan={2}>
                              costo target: €{fmt(fbCostoTarget)} · residuo: €{fmt(fbUnspecified)}
                            </td>
                            <td className="px-2 py-1 text-right text-[10px]" colSpan={3}>
                              {fbOvershoot && (<span className="text-rose-700 dark:text-rose-400 font-semibold" data-testid={`inv-cat-fb-mixed-needsreview-${c.codice}`}>⚠ NEEDS REVIEW</span>)}
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {preview.voci_needs_review_total > 0 && (
                <div className="text-[11px] text-amber-700 dark:text-amber-400 mt-2">
                  ⚠ {preview.voci_needs_review_total} voci da verificare (MDO INVILUPPO supera prezzo voce)
                </div>
              )}
            </>
          )}

          <DialogFooter className="flex gap-2 flex-wrap">
            {status?.applied && (
              <Button variant="outline" onClick={() => setConfirmReset(true)} disabled={loading} className="mr-auto border-rose-500 text-rose-700 dark:text-rose-300" data-testid="inv-reset">
                Rimuovi Auto
              </Button>
            )}
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="inv-cancel">Annulla</Button>
            <Button onClick={() => setConfirmApply(true)} disabled={!canEdit || loading || !preview} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="inv-apply">
              {status?.applied ? "Ricalcola Auto" : "Applica Compilazione Auto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmApply} onOpenChange={setConfirmApply}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Applicare la compilazione Auto INVILUPPO?</DialogTitle>
            <DialogDescription>
              Verranno salvati i costi previsti INVILUPPO (MDO, Materiali, Noli) per questo computo.
              <br /><br />
              <b>I prezzi e le quantità del computo originale non verranno modificati.</b>
              <br />
              STEP B (regole MDO/Materiali XPWE) e MaterialiPrevisti restano invariati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmApply(false)}>Annulla</Button>
            <Button onClick={doApply} disabled={loading} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="inv-apply-confirm">
              {loading ? "Applicazione…" : "Applica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rimuovere la compilazione Auto?</DialogTitle>
            <DialogDescription>
              Verranno cancellati tutti i valori Auto INVILUPPO applicati automaticamente.
              Eventuali valori marcati come "manual" saranno preservati.
              <br /><br />
              STEP B, XPWE, prezzi e quantità <b>non vengono modificati</b>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmReset(false)}>Annulla</Button>
            <Button onClick={doReset} disabled={loading} variant="destructive" data-testid="inv-reset-confirm">
              {loading ? "Rimozione…" : "Rimuovi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

